import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist/types/src/display/api';
import { Link } from 'react-router-dom';
import type {
  CampoExtraidoStatus,
  DocumentoStatus,
  HabitaCampoExtraido,
  HabitaCliente,
  HabitaDocumento,
  HabitaPendingFileItem,
  HabitaPendingItems,
  HabitaPendencia,
  HabitaProcessDetail,
  HabitaProcessStatusHistoryEntry,
  HabitaProcessStatusTransitionResponse,
  HabitaRequestDocumentAdjustmentResult,
  HabitaValidateFolderResult,
  PastaStatus,
  ProcessFileModel,
} from '../../../../shared/models';
import { PASTA_STATUS_LABELS, apiStatusToPastaStatus } from '../../../../shared/habitaStatusPresentation';
import { mapProcessDetailToHabitaCliente } from '../clienteFromDetail';
import {
  CopyValueButton,
  HabitaButton,
  HabitaCheckbox,
  HabitaDialog,
  HabitaSectionHeader,
  HabitaTextarea,
  HabitaTextInput,
  PastaStatusBadge,
  classNames,
  habitaControlBorderClass,
  habitaDividerClass,
  habitaSurfaceClass,
} from '../../../../shared/components/habita';
import {
  UNKNOWN_DOCUMENT_TYPE_LABEL,
  createZipBlob,
  downloadBlob,
  formatHabitaDateTime,
  isImageMimeType,
  isPdfMimeType,
  previewMimeTypeFromValues,
  resolvePreviewMimeType,
  uniqueZipEntryName,
  type BrowserZipEntry,
} from '../../../../shared/utils';
import {
  ProcessFileTypeExtractedView,
  normalizeDocType,
} from '../../../acompanhamento-processos/extracted/ProcessFileTypeExtractedView';

type FieldOverride = {
  valor: string;
  status: CampoExtraidoStatus;
  revisor: string;
  revisadoEm: string;
  valorOriginal: string;
  nota?: string;
};
type CampoView = HabitaCampoExtraido & { override?: FieldOverride };
type DocumentoView = Omit<HabitaDocumento, 'campos'> & { campos: CampoView[] };
type DocumentoCategoria = HabitaDocumento['categoria'];
type DocumentoGroup = {
  categoryLabel: string;
  categoria: DocumentoCategoria;
  documentos: DocumentoView[];
  hiddenDocumentos: DocumentoView[];
  id: string;
  label: string;
  statusCounts: Partial<Record<DocumentoStatus, number>>;
  visibleDocumentos: DocumentoView[];
};
type ReviewingState = { docId: string; campo: HabitaCampoExtraido } | null;
type DocumentPreviewLoadResult = { data: ArrayBuffer; mimeType?: string };
type DocumentPreviewLoader = (documento: HabitaDocumento) => Promise<DocumentPreviewLoadResult>;
type ProcessFilesLoader = (processId: string) => Promise<ProcessFileModel[]>;
type PendingItemsLoader = (processId: string) => Promise<HabitaPendingItems>;
type DocumentAdjustmentRequester = (request: {
  fileIds: number[];
  processId: string;
  rejectionReason: string;
}) => Promise<HabitaRequestDocumentAdjustmentResult>;
type PreAnalysisStatusTransitioner = (request: {
  motive?: string;
  processId: string;
  toStatus: PastaStatus;
}) => Promise<HabitaProcessStatusTransitionResponse>;
type FolderValidator = (processId: string) => Promise<HabitaValidateFolderResult>;
type DocumentPreviewKind = 'image' | 'pdf' | 'unsupported';
type DocumentPreviewState = {
  data: Uint8Array | null;
  error: string | null;
  fileName: string;
  kind: DocumentPreviewKind;
  loading: boolean;
  mimeType: string;
  url: string | null;
};
type DocumentViewerTransform = {
  rotation: number;
  zoom: number;
};
type ZipProgressState = {
  current: number;
  label: string;
  total: number;
} | null;
type ActionMessageVariant = 'error' | 'success' | 'warning';
type ActionMessageState = {
  message: string;
  variant: ActionMessageVariant;
} | null;
type TimelineAttachment = {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
};
type DetailTimelineEvent = HabitaCliente['timeline'][number] & {
  anexos?: TimelineAttachment[];
  justificativa?: string;
};
type PreAnalysisDecision = 'approve' | 'conditional' | 'reject';
type PreAnalysisDraftAttachment = {
  file: File;
  id: string;
};
type PreAnalysisDecisionConfig = {
  confirmLabel: string;
  description: string;
  eventDescription: string;
  icon: string;
  iconClassName: string;
  motivePrefix: string;
  outlineClassName: string;
  requiredJustification: boolean;
  successMessage: string;
  targetStatus: PastaStatus;
  title: string;
  variant: 'danger' | 'success' | 'warning';
};
type PdfJsModule = typeof import('pdfjs-dist');
type ResumoItem = {
  label: string;
  valor: string;
  origem: HabitaCampoExtraido['origem'];
  documento: string;
  docId?: string;
  sourceDetail?: Record<string, unknown> | null;
  status: CampoExtraidoStatus;
};
type AjusteDocumentalStatus = 'aguardando';
type AjusteDocumentalHistorico = {
  em: string;
  evento: string;
  por?: string;
};
type AjusteDocumental = {
  docId: string;
  docNome: string;
  docTipo: string;
  grupoId: string;
  historico: AjusteDocumentalHistorico[];
  id: string;
  justificativa: string;
  pendencia: HabitaPendencia;
  solicitadoEm: string;
  solicitadoPor: string;
  status: AjusteDocumentalStatus;
};
type ZipDocumentFailure = {
  documento: DocumentoView;
  error: unknown;
};

const EMPTY_PROCESS_FILES: ProcessFileModel[] = [];
const DOCUMENT_ZOOM_MIN = 0.5;
const DOCUMENT_ZOOM_MAX = 2.5;
const DOCUMENT_ZOOM_STEP = 0.25;
const DEFAULT_DOCUMENT_VIEWER_TRANSFORM: DocumentViewerTransform = { rotation: 0, zoom: 1 };
const actionMessageVariantClasses: Record<ActionMessageVariant, string> = {
  error: 'border-red-200 bg-red-50 text-red-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
};

const preAnalysisResultStatuses = new Set<PastaStatus>(['APROVADO', 'CONDICIONADO', 'REPROVADO']);

const preAnalysisDecisionConfigs: Record<PreAnalysisDecision, PreAnalysisDecisionConfig> = {
  approve: {
    confirmLabel: 'Confirmar aprovação',
    description: 'Registre a decisão de aprovação da pré-análise de crédito.',
    eventDescription: 'Aprovação',
    icon: 'ri-shield-check-line',
    iconClassName: 'text-emerald-700 dark:text-emerald-200',
    motivePrefix: 'Pré-análise aprovada.',
    outlineClassName: 'border-emerald-300/70 text-emerald-700 hover:bg-emerald-500/10 dark:border-emerald-400/40 dark:text-emerald-200',
    requiredJustification: false,
    successMessage: 'Pasta aprovada na pré-análise.',
    targetStatus: 'APROVADO',
    title: 'Aprovar pasta',
    variant: 'success',
  },
  conditional: {
    confirmLabel: 'Confirmar condicional',
    description: 'Registre a aprovação com condicional da pré-análise de crédito.',
    eventDescription: 'Aprovação com Condicional',
    icon: 'ri-shield-flash-line',
    iconClassName: 'text-amber-700 dark:text-amber-200',
    motivePrefix: 'Pré-análise aprovada com condicional.',
    outlineClassName: 'border-amber-300/70 text-amber-800 hover:bg-amber-500/10 dark:border-amber-400/40 dark:text-amber-200',
    requiredJustification: true,
    successMessage: 'Pasta aprovada com condicional.',
    targetStatus: 'CONDICIONADO',
    title: 'Aprovar com condicional',
    variant: 'warning',
  },
  reject: {
    confirmLabel: 'Confirmar reprovação',
    description: 'Registre a reprovação da pré-análise de crédito.',
    eventDescription: 'Reprovação',
    icon: 'ri-shield-cross-line',
    iconClassName: 'text-red-700 dark:text-red-200',
    motivePrefix: 'Pré-análise reprovada.',
    outlineClassName: 'border-red-300/70 text-red-700 hover:bg-red-500/10 dark:border-red-400/40 dark:text-red-200',
    requiredJustification: true,
    successMessage: 'Pasta reprovada na pré-análise.',
    targetStatus: 'REPROVADO',
    title: 'Reprovar pasta',
    variant: 'danger',
  },
};

const preAnalysisDecisionByStatus: Partial<Record<PastaStatus, PreAnalysisDecision>> = {
  APROVADO: 'approve',
  CONDICIONADO: 'conditional',
  REPROVADO: 'reject',
};

function externalUrlConfig(envValue: string | undefined): string | undefined {
  const cleanEnvValue = envValue?.trim();
  return cleanEnvValue || undefined;
}

const CRM_CONTACT_BASE_URL = externalUrlConfig(import.meta.env.VITE_CRM_CONTACT_BASE_URL as string | undefined);
const CHATWOOT_CONTACT_BASE_URL = externalUrlConfig(
  import.meta.env.VITE_CHATWOOT_CONTACT_BASE_URL as string | undefined
);

const campoStatusClasses: Record<CampoExtraidoStatus, string> = {
  validado: 'border-emerald-200/90 bg-emerald-50/80 text-emerald-800',
  revisao: 'border-amber-200/90 bg-amber-50/80 text-amber-800',
  rejeitado: 'border-red-200/90 bg-red-50 text-red-800',
  sem_validacao: 'border-slate-200 bg-slate-50 text-slate-700',
};

const campoStatusLabels: Record<CampoExtraidoStatus, string> = {
  validado: 'Validado',
  revisao: 'Revisão',
  rejeitado: 'Rejeitado',
  sem_validacao: 'Sem validação',
};

const documentoStatusLabels: Record<DocumentoStatus, string> = {
  recebido: 'Recebido',
  em_analise: 'Em análise Chavinha',
  validado: 'Validado',
  reprovado: 'Reprovado',
  revisao: 'Revisão manual',
  expirado: 'Expirado',
  desconhecido: 'Status desconhecido',
};

const documentoStatusClasses: Record<DocumentoStatus, string> = {
  recebido: 'border-slate-300/60 bg-slate-500/10 text-slate-700 dark:text-slate-200',
  em_analise: 'border-sky-300/60 bg-sky-500/10 text-sky-800 dark:text-sky-200',
  validado: 'border-emerald-300/60 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200',
  reprovado: 'border-red-300/60 bg-red-500/10 text-red-800 dark:text-red-200',
  revisao: 'border-amber-300/60 bg-amber-500/10 text-amber-800 dark:text-amber-200',
  expirado: 'border-slate-300/60 bg-slate-500/10 text-slate-600 dark:text-slate-300',
  desconhecido: 'border-slate-300/60 bg-slate-500/10 text-slate-700 dark:text-slate-200',
};

const ajusteDocumentalStatusLabels: Record<AjusteDocumentalStatus, string> = {
  aguardando: 'Aguardando reenvio',
};

const ajusteDocumentalStatusClasses: Record<AjusteDocumentalStatus, string> = {
  aguardando: 'border-amber-200 bg-amber-50 text-amber-800',
};

const documentoStatusOrder: DocumentoStatus[] = [
  'desconhecido',
  'reprovado',
  'revisao',
  'em_analise',
  'recebido',
  'validado',
  'expirado',
];

const documentoCategoriaLabels: Record<DocumentoCategoria, string> = {
  identificacao: 'Identificação',
  renda: 'Renda',
  residencia: 'Residência',
  estado_civil: 'Estado civil',
  outro: 'Outros documentos',
};

const documentoCategoriaIcons: Record<DocumentoCategoria, string> = {
  identificacao: 'ri-profile-line',
  renda: 'ri-money-dollar-circle-line',
  residencia: 'ri-home-5-line',
  estado_civil: 'ri-file-list-3-line',
  outro: 'ri-file-text-line',
};

const documentoCategoriaOrder: DocumentoCategoria[] = ['identificacao', 'renda', 'residencia', 'estado_civil', 'outro'];
const hideableDocumentoStatuses = new Set<DocumentoStatus>(['reprovado', 'expirado']);

function now(): string {
  return new Date().toLocaleString('pt-BR');
}

function fieldKey(docId: string, label: string): string {
  return `${docId}::${label}`;
}

function extractedValueIsFilled(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    return text !== '' && text !== '—' && text !== '-' && text !== 'null' && text !== 'undefined';
  }
  if (typeof value === 'number') return Number.isFinite(value);
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.some(extractedValueIsFilled);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some(extractedValueIsFilled);
  }
  return false;
}

function filledFieldRowsFirst<T extends { valor?: unknown }>(items: readonly T[]): T[] {
  return items
    .map((item, index) => ({
      filled: extractedValueIsFilled(item.valor),
      index,
      item,
    }))
    .sort((left, right) => Number(right.filled) - Number(left.filled) || left.index - right.index)
    .map(({ item }) => item);
}

function cleanUrlValue(value: string | null | undefined): string | null {
  if (!value || value === '-') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function appendUrlPath(baseUrl: string | undefined, value: string | null | undefined): string | null {
  const cleanValue = cleanUrlValue(value);
  if (!baseUrl || !cleanValue) return null;
  return `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(cleanValue)}`;
}

function buildCrmContactUrl(cliente: HabitaCliente): string | null {
  return appendUrlPath(CRM_CONTACT_BASE_URL, cliente.crmContactId);
}

function buildChatwootContactUrl(cliente: HabitaCliente): string | null {
  return appendUrlPath(CHATWOOT_CONTACT_BASE_URL, cliente.chatwootContactId);
}

function buildContactUrl(cliente: HabitaCliente): string | null {
  return buildCrmContactUrl(cliente);
}

function ExternalUrlAction({
  children,
  disabledTitle,
  icon,
  size = 'sm',
  title,
  url,
  variant = 'secondary',
}: {
  children?: ReactNode;
  disabledTitle: string;
  icon: string;
  size?: 'sm' | 'icon';
  title: string;
  url: string | null;
  variant?: 'secondary' | 'ghost';
}) {
  const className = classNames(
    'inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-instrument font-medium transition-colors',
    variant === 'secondary'
      ? `border ${habitaControlBorderClass} bg-card text-textPrimary hover:bg-accent`
      : 'text-textSecondary hover:bg-accent hover:text-textPrimary',
    size === 'sm' ? 'h-8 px-3 text-xs' : 'h-8 w-8 p-0 text-sm',
    !url && 'cursor-not-allowed opacity-75'
  );
  const content = (
    <>
      <i className={`${icon} text-base`} aria-hidden />
      {children}
    </>
  );

  if (!url) {
    return (
      <button type="button" disabled className={className} title={disabledTitle}>
        {content}
      </button>
    );
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      title={title}
      aria-label={typeof children === 'undefined' ? title : undefined}
    >
      {content}
    </a>
  );
}

function FieldStatusBadge({ status }: { status: CampoExtraidoStatus }) {
  if (status === 'validado') return null;

  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${campoStatusClasses[status]}`}>
      {campoStatusLabels[status]}
    </span>
  );
}

function confidenceLabel(value: number | null | undefined): string {
  return value === null || value === undefined
    ? 'Confiança não informada'
    : `Confiança ${value}%`;
}

function DocumentoStatusBadge({ status }: { status: DocumentoStatus }) {
  return (
    <span className={`inline-flex shrink-0 rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${documentoStatusClasses[status]}`}>
      {documentoStatusLabels[status]}
    </span>
  );
}

function DocumentRejectionReasonCard({ documento }: { documento: DocumentoView }) {
  return (
    <div className="rounded-md border border-red-300/60 bg-red-500/10 p-3 font-instrument">
      <div className="flex items-start gap-2">
        <i className="ri-error-warning-line mt-0.5 shrink-0 text-base text-red-700 dark:text-red-200" aria-hidden />
        <div className="min-w-0">
          <p className="text-xs font-semibold text-red-800 dark:text-red-100">
            Documento recusado
          </p>
          <p className="mt-1 text-sm leading-relaxed text-red-900 dark:text-red-100">
            {documento.motivoRejeicao}
          </p>
        </div>
      </div>
    </div>
  );
}

function AjusteDocumentalStatusBadge({ status }: { status: AjusteDocumentalStatus }) {
  return (
    <span className={`inline-flex shrink-0 rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${ajusteDocumentalStatusClasses[status]}`}>
      {ajusteDocumentalStatusLabels[status]}
    </span>
  );
}

function DocumentoStatusCounter({ count, status }: { count: number; status: DocumentoStatus }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${documentoStatusClasses[status]}`}
    >
      {documentoStatusLabels[status]}
      <span className="font-semibold">{count}</span>
    </span>
  );
}

function countDocumentStatus(documentos: DocumentoView[]): Partial<Record<DocumentoStatus, number>> {
  return documentos.reduce<Partial<Record<DocumentoStatus, number>>>((counts, documento) => {
    counts[documento.status] = (counts[documento.status] ?? 0) + 1;
    return counts;
  }, {});
}

function documentoTypeLabel(documento: DocumentoView): string {
  const cleanTipo = documento.tipo?.trim();
  return cleanTipo || UNKNOWN_DOCUMENT_TYPE_LABEL;
}

function documentoIconClassName(documento: Pick<DocumentoView, 'categoria'>): string {
  return documentoCategoriaIcons[documento.categoria];
}

function pendenciaTipoFromDocumentoStatus(status: DocumentoStatus): HabitaPendencia['tipo'] {
  if (status === 'expirado') return 'expirado';
  if (status === 'revisao') return 'revisao';
  if (status === 'reprovado') return 'documento_invalido';
  return 'documento_invalido';
}

function pendenciaSeveridadeFromDocumentoStatus(status: DocumentoStatus): HabitaPendencia['severidade'] {
  if (status === 'reprovado' || status === 'expirado') return 'alta';
  if (status === 'revisao') return 'media';
  return 'baixa';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function cleanString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function firstRuleCheckMessage(value: unknown): string | null {
  if (!isRecord(value)) return null;

  const directMessage = cleanString(value.message) ?? cleanString(value.error);
  if (directMessage) return directMessage;

  for (const item of Object.values(value)) {
    if (isRecord(item)) {
      const nestedMessage = cleanString(item.message) ?? cleanString(item.error) ?? firstRuleCheckMessage(item);
      if (nestedMessage) return nestedMessage;
    }
  }

  return null;
}

function collectPayloadMessages(value: unknown): string[] {
  const directMessage = cleanString(value);
  if (directMessage) return [directMessage];

  if (Array.isArray(value)) {
    return value.flatMap(collectPayloadMessages);
  }

  if (!isRecord(value)) return [];

  const messages = [
    cleanString(value.message),
    cleanString(value.msg),
    cleanString(value.error),
    cleanString(value.stage),
    cleanString(value.transition_error),
  ].filter((message): message is string => Boolean(message));

  const nestedMessages = Object.values(value)
    .filter((item) => isRecord(item) || Array.isArray(item))
    .flatMap(collectPayloadMessages);

  return Array.from(new Set([...messages, ...nestedMessages]));
}

function errorMessagesFromUnknown(error: unknown): string[] {
  const payload = isRecord(error) && 'data' in error ? error.data : null;
  const payloadMessages = collectPayloadMessages(payload);
  const errorMessage = error instanceof Error ? cleanString(error.message) : null;

  return Array.from(new Set([
    ...payloadMessages,
    ...(errorMessage ? [errorMessage] : []),
  ]));
}

function normalizeErrorText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR');
}

function userFacingErrorMessage(error: unknown, fallback: string): string {
  return errorMessagesFromUnknown(error)
    .find((message) => !/^HTTP Error:/i.test(message))
    ?? fallback;
}

function rejectionSourceLabel(value: string | null | undefined): string {
  if (value === 'sdr') return 'SDR';
  if (value === 'rule') return 'Regra automática';
  if (value === 'gatekeeper') return 'Gatekeeper IA';
  return 'Chavinha';
}

function pendingFileReason(file: HabitaPendingFileItem): string {
  return file.rejection_reason?.trim()
    || firstRuleCheckMessage(file.rule_check_data)
    || 'Documento precisa de ajuste.';
}

function createAjusteDocumentalFromPendingFile({
  documento,
  file,
  processId,
}: {
  documento?: DocumentoView;
  file: HabitaPendingFileItem;
  processId: string;
}): AjusteDocumental {
  const docId = String(file.process_file_id);
  const justificativa = pendingFileReason(file);
  const solicitadoEm = formatHabitaDateTime(file.rejected_at ?? '');
  const solicitadoPor = rejectionSourceLabel(file.rejection_source);
  const docTipo = file.doc_subtype?.trim()
    || file.doc_type?.trim()
    || (documento ? documentoTypeLabel(documento) : UNKNOWN_DOCUMENT_TYPE_LABEL);
  const documentStatus = documento?.status ?? 'reprovado';

  return {
    docId,
    docNome: file.name?.trim() || documento?.nome || `Documento #${docId}`,
    docTipo,
    grupoId: processId,
    historico: [
      {
        em: solicitadoEm,
        evento: 'Ajuste pendente',
        por: solicitadoPor,
      },
    ],
    id: `pending-file-${processId}-${docId}`,
    justificativa,
    pendencia: {
      descricao: justificativa,
      id: `pendencia-${processId}-${docId}`,
      severidade: pendenciaSeveridadeFromDocumentoStatus(documentStatus),
      tipo: pendenciaTipoFromDocumentoStatus(documentStatus),
    },
    solicitadoEm,
    solicitadoPor,
    status: 'aguardando',
  };
}

function buildAjustesDocumentaisFromPendingItems(
  pendingItems: HabitaPendingItems | null,
  documentos: DocumentoView[],
  processId: string
): AjusteDocumental[] {
  if (!pendingItems) return [];

  const documentosById = new Map(documentos.map((documento) => [documento.id, documento]));
  return pendingItems.pending_files.map((file) => (
    createAjusteDocumentalFromPendingFile({
      documento: documentosById.get(String(file.process_file_id)),
      file,
      processId,
    })
  ));
}

function documentoGroupId(documento: DocumentoView): string {
  return `${documento.categoria}::${documentoTypeLabel(documento).toLocaleLowerCase('pt-BR')}`;
}

function shouldHideDocumento(documento: DocumentoView, documentos: DocumentoView[], showHidden: boolean): boolean {
  if (showHidden || !hideableDocumentoStatuses.has(documento.status)) return false;
  return documentos.some((groupDocumento) => !hideableDocumentoStatuses.has(groupDocumento.status));
}

function buildDocumentoGroups(
  documentos: DocumentoView[],
  showHiddenDocumentosByGroup: Record<string, boolean>
): DocumentoGroup[] {
  const groups = new Map<string, Pick<DocumentoGroup, 'categoria' | 'documentos' | 'id' | 'label'>>();

  documentos.forEach((documento) => {
    const id = documentoGroupId(documento);
    const existingGroup = groups.get(id);
    if (existingGroup) {
      existingGroup.documentos.push(documento);
      return;
    }

    groups.set(id, {
      categoria: documento.categoria,
      documentos: [documento],
      id,
      label: documentoTypeLabel(documento),
    });
  });

  return Array.from(groups.values())
    .sort((a, b) => {
      const categoryDiff = documentoCategoriaOrder.indexOf(a.categoria) - documentoCategoriaOrder.indexOf(b.categoria);
      if (categoryDiff !== 0) return categoryDiff;
      return a.label.localeCompare(b.label, 'pt-BR');
    })
    .map((group) => {
      const showHidden = Boolean(showHiddenDocumentosByGroup[group.id]);
      const hiddenDocumentos = group.documentos.filter((documento) => (
        shouldHideDocumento(documento, group.documentos, false)
      ));
      const visibleDocumentos = group.documentos.filter((documento) => (
        showHidden || !hiddenDocumentos.includes(documento)
      ));

      return {
        ...group,
        categoryLabel: documentoCategoriaLabels[group.categoria],
        hiddenDocumentos,
        statusCounts: countDocumentStatus(group.documentos),
        visibleDocumentos,
      };
    });
}

function buildValidationDocumentSummary(documentos: DocumentoView[]) {
  const groups = new Map<string, {
    categoria: DocumentoCategoria;
    id: string;
    label: string;
    quantity: number;
    statuses: Partial<Record<DocumentoStatus, number>>;
  }>();

  documentos.forEach((documento) => {
    const id = documentoGroupId(documento);
    const existingGroup = groups.get(id);
    const group = existingGroup ?? {
      categoria: documento.categoria,
      id,
      label: documentoTypeLabel(documento),
      quantity: 0,
      statuses: {},
    };

    group.quantity += 1;
    group.statuses[documento.status] = (group.statuses[documento.status] ?? 0) + 1;
    groups.set(id, group);
  });

  return Array.from(groups.values()).sort((left, right) => {
    const categoryDiff = documentoCategoriaOrder.indexOf(left.categoria) - documentoCategoriaOrder.indexOf(right.categoria);
    if (categoryDiff !== 0) return categoryDiff;
    return left.label.localeCompare(right.label, 'pt-BR');
  });
}

function formatTimelineDate(value: string): string {
  return formatHabitaDateTime(value).replace(',', ' às');
}

function timestampOf(value: string | null | undefined): number {
  const timestamp = Date.parse(value ?? '');
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function localAttachmentId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
  return `${(size / (1024 * 1024)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

function preAnalysisDescription(toStatus: PastaStatus, fromStatus: PastaStatus): string | null {
  if (fromStatus === 'PRE_ANALISE_CREDITO' && toStatus === 'APROVADO') return 'Aprovação';
  if (fromStatus === 'PRE_ANALISE_CREDITO' && toStatus === 'CONDICIONADO') return 'Aprovação com Condicional';
  if (fromStatus === 'PRE_ANALISE_CREDITO' && toStatus === 'REPROVADO') return 'Reprovação';
  if (preAnalysisResultStatuses.has(fromStatus) && toStatus === 'PRE_ANALISE_CREDITO') return 'Correção de Resultado';
  return null;
}

function buildPreAnalysisMotive({
  attachmentNames,
  justification,
  prefix,
}: {
  attachmentNames?: string[];
  justification?: string;
  prefix: string;
}): string {
  const parts = [prefix];
  const cleanJustification = justification?.trim();
  const cleanAttachmentNames = attachmentNames?.filter(Boolean) ?? [];

  if (cleanJustification) {
    parts.push(`Justificativa: ${cleanJustification}`);
  }

  if (cleanAttachmentNames.length > 0) {
    parts.push(`Anexos: ${cleanAttachmentNames.join(', ')}`);
  }

  return parts.join(' ');
}

function timelineJustificationFromMotive(motive: string | null | undefined): string | undefined {
  const cleanMotive = motive?.trim();
  if (!cleanMotive) return undefined;

  const justificationMarker = 'Justificativa:';
  const attachmentMarker = 'Anexos:';
  const justificationStart = cleanMotive.indexOf(justificationMarker);

  if (justificationStart === -1) {
    const decisionPrefixes = [
      preAnalysisDecisionConfigs.approve.motivePrefix,
      preAnalysisDecisionConfigs.conditional.motivePrefix,
      preAnalysisDecisionConfigs.reject.motivePrefix,
    ];

    if (decisionPrefixes.some((prefix) => cleanMotive === prefix || cleanMotive.startsWith(`${prefix} ${attachmentMarker}`))) {
      return undefined;
    }

    return cleanMotive;
  }

  const valueStart = justificationStart + justificationMarker.length;
  const attachmentStart = cleanMotive.indexOf(attachmentMarker, valueStart);
  const valueEnd = attachmentStart === -1 ? cleanMotive.length : attachmentStart;
  const justification = cleanMotive.slice(valueStart, valueEnd).trim();

  return justification || undefined;
}

function buildStatusHistoryTimeline(
  statusHistory: HabitaProcessStatusHistoryEntry[]
): DetailTimelineEvent[] {
  return statusHistory
    .filter((entry) => entry.changed_at && entry.to_status)
    .map((entry) => {
      const toStatus = apiStatusToPastaStatus(entry.to_status);
      const fromStatus = apiStatusToPastaStatus(entry.from_status);
      const isFolderValidation = fromStatus === 'VALIDACAO_PASTA' && toStatus === 'PRE_ANALISE_CREDITO';
      const preAnalysisEvent = preAnalysisDescription(toStatus, fromStatus);
      const motive = entry.motive?.trim();

      return {
        data: entry.changed_at ?? '',
        tipo: preAnalysisEvent ? 'aprovacao' : 'validacao',
        descricao: preAnalysisEvent ?? (
          isFolderValidation
            ? 'Pasta validada'
            : `Status alterado para ${PASTA_STATUS_LABELS[toStatus]}`
        ),
        usuario: entry.changed_by?.trim() || undefined,
        justificativa: preAnalysisEvent
          ? timelineJustificationFromMotive(motive)
          : isFolderValidation
            ? motive || 'Pasta validada e encaminhada para Pré-análise.'
            : motive || undefined,
      } satisfies DetailTimelineEvent;
    });
}

function PastaTimelineSection({ events }: { events: DetailTimelineEvent[] }) {
  const orderedEvents = [...events].sort((left, right) => (
    timestampOf(right.data) - timestampOf(left.data)
  ));

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Histórico da Pré-Análise" icon="ri-history-line" />
      <div className="space-y-3 p-4">
        {orderedEvents.length === 0 ? (
          <div className="rounded-md border border-dashed border-border-subtle p-3 font-instrument text-sm text-textSecondary">
            Nenhum evento registrado para esta pasta.
          </div>
        ) : orderedEvents.map((event, index) => (
          <div key={`${event.data}-${event.descricao}-${index}`} className="flex gap-3">
            <span className="mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-primary">
              <i className="ri-checkbox-circle-line text-sm" aria-hidden />
            </span>
            <div className="min-w-0 flex-1 rounded-md border border-border-subtle bg-card px-3 py-2">
              <p className="font-instrument text-sm font-semibold text-textPrimary">{event.descricao}</p>
              <p className="mt-0.5 font-instrument text-xs text-textSecondary">
                {formatTimelineDate(event.data)}
                {event.usuario ? ` · ${event.usuario}` : ''}
              </p>
              {event.justificativa && (
                <p className="mt-1 font-instrument text-xs text-textSecondary">
                  Justificativa: {event.justificativa}
                </p>
              )}
              {event.anexos && event.anexos.length > 0 && (
                <div className="mt-2 space-y-1">
                  <p className="font-instrument text-[11px] font-semibold uppercase tracking-wide text-textSecondary">
                    Anexos
                  </p>
                  <div className="space-y-1">
                    {event.anexos.map((anexo) => (
                      <div key={anexo.id} className="flex flex-col gap-1 rounded-md border border-border-subtle bg-muted px-2 py-1.5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="min-w-0">
                          <p className="truncate font-instrument text-xs font-medium text-textPrimary">{anexo.name}</p>
                          <p className="font-instrument text-[11px] text-textSecondary">{formatFileSize(anexo.size)}</p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <a
                            href={anexo.url}
                            rel="noreferrer"
                            target="_blank"
                            className="font-instrument text-xs font-medium text-primary hover:underline"
                          >
                            Visualizar
                          </a>
                          <a
                            download={anexo.name}
                            href={anexo.url}
                            className="font-instrument text-xs font-medium text-primary hover:underline"
                          >
                            Baixar
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AjustesDocumentaisSection({
  ajustes,
  error,
  loading,
  onReload,
}: {
  ajustes: AjusteDocumental[];
  error: string | null;
  loading: boolean;
  onReload: () => void;
}) {
  const ajustesAbertos = ajustes.filter((ajuste) => ajuste.status === 'aguardando');

  return (
    <section className={habitaSurfaceClass}>
      <HabitaSectionHeader title="Ajustes documentais" icon="ri-tools-line">
        {loading && (
          <span className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-card px-2 py-0.5 font-instrument text-[10px] font-semibold text-textSecondary">
            <i className="ri-loader-4-line animate-spin" aria-hidden />
            Carregando
          </span>
        )}
        {ajustesAbertos.length > 0 && (
          <span className="inline-flex rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 font-instrument text-[10px] font-semibold text-amber-800">
            {ajustesAbertos.length} aberto{ajustesAbertos.length > 1 ? 's' : ''}
          </span>
        )}
      </HabitaSectionHeader>

      <div className="p-5">
        {error ? (
          <div className="rounded-md border border-amber-300/60 bg-amber-500/10 p-3">
            <p className="font-instrument text-sm text-amber-900 dark:text-amber-100">{error}</p>
            <HabitaButton
              className="mt-2"
              icon="ri-refresh-line"
              onClick={onReload}
              size="sm"
              variant="ghost"
            >
              Tentar novamente
            </HabitaButton>
          </div>
        ) : ajustes.length === 0 ? (
          <div className="py-8 text-center">
            <i className="ri-inbox-line text-4xl text-textSecondary/60" aria-hidden />
            <p className="mt-2 font-instrument text-sm text-textSecondary">
              Nenhum ajuste documental pendente.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {ajustes.map((ajuste) => (
              <div key={ajuste.id} className="rounded-lg border border-border-subtle bg-muted p-3">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                      <i className="ri-file-warning-line text-base" aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <p className="font-instrument text-sm font-semibold text-textPrimary">{ajuste.docNome}</p>
                      <p className="mt-0.5 font-instrument text-[11px] text-textSecondary">
                        {ajuste.docTipo} · Solicitado por {ajuste.solicitadoPor} em {ajuste.solicitadoEm}
                      </p>
                    </div>
                  </div>
                  <AjusteDocumentalStatusBadge status={ajuste.status} />
                </div>

                <div className="mt-3 rounded-md border border-border-subtle bg-card p-3">
                  <p className="font-instrument text-[10px] uppercase tracking-wide text-textSecondary">
                    Justificativa interna
                  </p>
                  <p className="mt-1 whitespace-pre-wrap break-words font-instrument text-sm text-textPrimary">
                    {ajuste.justificativa}
                  </p>
                </div>

                <details className="mt-3 rounded-md border border-border-subtle bg-card/70">
                  <summary className="cursor-pointer px-3 py-2 font-instrument text-xs font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary">
                    Histórico ({ajuste.historico.length})
                  </summary>
                  <div className="space-y-2 border-t border-border-subtle px-3 py-2">
                    {ajuste.historico.map((item, index) => (
                      <div key={`${ajuste.id}-${index}`} className="flex items-start gap-2 font-instrument text-xs text-textSecondary">
                        <i className="ri-checkbox-blank-circle-fill mt-1 text-[6px] text-primary" aria-hidden />
                        <p className="min-w-0">
                          <span className="font-medium text-textPrimary">{item.evento}</span>
                          <span> · {item.em}</span>
                          {item.por && <span> · {item.por}</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </details>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function fileExtensionFromValue(value: string | null | undefined): string | null {
  const cleanValue = value?.trim().split(/[?#]/)[0] ?? '';
  const fileName = cleanValue.split('/').filter(Boolean).pop() ?? cleanValue;
  const match = fileName.match(/\.([a-zA-Z0-9]{2,8})$/);
  return match ? match[1] : null;
}

function hasFileExtension(value: string): boolean {
  return /\.[a-zA-Z0-9]{2,8}$/.test(value.trim());
}

function safeDownloadFileName(value: string): string {
  return value.replace(/[\\/:*?"<>|]+/g, '-').trim();
}

function fileNameFromDocumento(documento: DocumentoView): string {
  const cleanName = documento.nome?.trim();
  if (cleanName) {
    const safeName = safeDownloadFileName(cleanName);
    if (hasFileExtension(safeName)) return safeName;

    const extension = fileExtensionFromValue(documento.arquivoNomeOriginal) ?? fileExtensionFromValue(documento.path);
    return extension ? `${safeName}.${extension}` : safeName;
  }

  const cleanPath = (documento.arquivoNomeOriginal ?? documento.path)?.split(/[?#]/)[0] ?? '';
  const pathName = cleanPath.split('/').filter(Boolean).pop();
  return pathName || `documento-${documento.id}`;
}

function zipFileNameFromCliente(cliente: HabitaCliente): string {
  const processId = safeDownloadFileName(cliente.processoId.replace(/^#/, '').trim()) || cliente.id;
  const clienteName = safeDownloadFileName(cliente.nome).replace(/\s+/g, '-').toLowerCase() || 'cliente';
  return `pasta-${processId}-${clienteName}.zip`;
}

function fallbackMimeType(documento: DocumentoView): string {
  return previewMimeTypeFromValues(documento.arquivoNomeOriginal, documento.path, documento.nome) ?? 'application/octet-stream';
}

function shouldRenderStructuredExtractedView(documento: DocumentoView): boolean {
  const extractedData = documento.file?.extracted_data;
  const docType = documento.file?.doc_type;

  return Boolean(extractedData && normalizeDocType(docType) !== 'OUTRO');
}

function previewKindFromMimeType(mimeType: string): DocumentPreviewKind {
  if (isPdfMimeType(mimeType)) return 'pdf';
  if (isImageMimeType(mimeType)) return 'image';
  return 'unsupported';
}

function clampDocumentZoom(value: number): number {
  return Math.min(DOCUMENT_ZOOM_MAX, Math.max(DOCUMENT_ZOOM_MIN, value));
}

function normalizeDocumentRotation(value: number): number {
  return ((value % 360) + 360) % 360;
}

function documentViewerErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') return error.message;
  return 'Não foi possível renderizar o documento.';
}

function createZipCancelledError(): Error {
  const error = new Error('Geração do ZIP cancelada.');
  error.name = 'ZipGenerationCancelled';
  return error;
}

function isZipCancelledError(error: unknown): boolean {
  return error instanceof Error && error.name === 'ZipGenerationCancelled';
}

function sanitizeFileAccessErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message.trim() : '';

  if (message === '') return 'Falha ao encontrar o arquivo.';

  if (
    message.includes('Falha ao encontrar o arquivo') ||
    message.includes('sem conteúdo de arquivo') ||
    message.includes('sem conteúdo') ||
    message.includes('arquivo vazio') ||
    message.includes('JSON sem arquivo') ||
    message.includes('base64') ||
    message.includes('binário') ||
    message.includes('/webhook/') ||
    message.includes('/process/file/') ||
    message.includes('A rota ') ||
    /HTTP Error:\s*(?:404|500|502|503|504)\b/.test(message)
  ) {
    return 'Falha ao encontrar o arquivo.';
  }

  if (message.includes('identificador numérico')) {
    return 'Arquivo sem identificador para download.';
  }

  return 'Falha ao carregar o arquivo.';
}

function zipFailureReason(error: unknown): string {
  return sanitizeFileAccessErrorMessage(error);
}

function formatZipFailureMessage(failures: ZipDocumentFailure[]): string {
  const visibleFailures = failures.slice(0, 3).map(({ documento, error }) => (
    `${documento.nome} (ID ${documento.id}): ${zipFailureReason(error)}`
  ));
  const hiddenCount = failures.length - visibleFailures.length;

  return hiddenCount > 0
    ? `${visibleFailures.join('; ')}; e mais ${hiddenCount} arquivo${hiddenCount === 1 ? '' : 's'}`
    : visibleFailures.join('; ');
}

function DocumentToolbarIconButton({
  disabled = false,
  icon,
  onClick,
  title,
}: {
  disabled?: boolean;
  icon: string;
  onClick: () => void;
  title: string;
}) {
  return (
    <button
      type="button"
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className="inline-flex h-8 w-8 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-card hover:text-textPrimary disabled:cursor-not-allowed disabled:opacity-45"
    >
      <i className={`${icon} text-base`} aria-hidden />
    </button>
  );
}

function DocumentImageViewer({
  alt,
  compact,
  rotation,
  src,
  zoom,
}: {
  alt: string;
  compact: boolean;
  rotation: number;
  src: string;
  zoom: number;
}) {
  const [imageSize, setImageSize] = useState<{ height: number; width: number } | null>(null);
  const normalizedRotation = normalizeDocumentRotation(rotation);

  useEffect(() => {
    setImageSize(null);
  }, [src]);

  const imageFrame = useMemo(() => {
    if (!imageSize) return null;

    const maxBaseWidth = compact ? 860 : 760;
    const maxBaseHeight = compact ? 720 : 640;
    const fitScale = Math.min(maxBaseWidth / imageSize.width, maxBaseHeight / imageSize.height, 1);
    const displayWidth = Math.max(1, imageSize.width * fitScale * zoom);
    const displayHeight = Math.max(1, imageSize.height * fitScale * zoom);
    const isSideways = normalizedRotation === 90 || normalizedRotation === 270;

    return {
      frameHeight: isSideways ? displayWidth : displayHeight,
      frameWidth: isSideways ? displayHeight : displayWidth,
      imageHeight: displayHeight,
      imageWidth: displayWidth,
    };
  }, [compact, imageSize, normalizedRotation, zoom]);

  return (
    <div className="flex flex-1 items-start justify-center overflow-auto bg-muted p-4">
      <div
        className={classNames(
          'shrink-0',
          imageFrame ? 'relative' : 'flex min-h-full w-full items-center justify-center'
        )}
        style={imageFrame ? { height: imageFrame.frameHeight, width: imageFrame.frameWidth } : undefined}
      >
        <img
          src={src}
          alt={alt}
          onLoad={(event) => {
            const image = event.currentTarget;
            setImageSize({ height: image.naturalHeight, width: image.naturalWidth });
          }}
          className={classNames(
            'rounded border border-border-subtle bg-card object-contain shadow-sm',
            imageFrame ? 'absolute left-1/2 top-1/2 max-w-none' : 'max-h-full max-w-full'
          )}
          style={imageFrame ? {
            height: imageFrame.imageHeight,
            transform: `translate(-50%, -50%) rotate(${normalizedRotation}deg)`,
            transformOrigin: 'center center',
            width: imageFrame.imageWidth,
          } : undefined}
        />
      </div>
    </div>
  );
}

function PdfPageCanvas({
  pageNumber,
  pdfDocument,
  rotation,
  zoom,
}: {
  pageNumber: number;
  pdfDocument: PDFDocumentProxy;
  rotation: number;
  zoom: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let renderTask: RenderTask | null = null;

    async function renderPage() {
      const canvas = canvasRef.current;
      if (!canvas) return;

      setIsRendering(true);
      setRenderError(null);

      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ rotation, scale: zoom });
        const outputScale = window.devicePixelRatio || 1;
        const context = canvas.getContext('2d');

        if (!context) {
          throw new Error('Canvas indisponível para renderizar o PDF.');
        }

        canvas.width = Math.floor(viewport.width * outputScale);
        canvas.height = Math.floor(viewport.height * outputScale);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        context.clearRect(0, 0, canvas.width, canvas.height);

        renderTask = page.render({
          canvasContext: context,
          transform: outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : undefined,
          viewport,
        });

        await renderTask.promise;

        if (!cancelled) {
          page.cleanup();
          setIsRendering(false);
        }
      } catch (error) {
        if (!cancelled) {
          setRenderError(documentViewerErrorMessage(error));
          setIsRendering(false);
        }
      }
    }

    void renderPage();

    return () => {
      cancelled = true;
      renderTask?.cancel();
    };
  }, [pageNumber, pdfDocument, rotation, zoom]);

  return (
    <div className="relative shrink-0 rounded-md border border-border-subtle bg-card p-2 shadow-sm">
      <canvas ref={canvasRef} className={classNames('block bg-white', isRendering && 'opacity-40')} />
      {isRendering && (
        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-card/70">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary" aria-hidden />
        </div>
      )}
      {renderError && (
        <div className="absolute inset-0 flex items-center justify-center rounded-md bg-card/95 p-4 text-center">
          <div>
            <i className="ri-file-warning-line text-2xl text-amber-600" aria-hidden />
            <p className="mt-2 max-w-[260px] font-instrument text-xs text-textSecondary">{renderError}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function DocumentPdfViewer({
  compact,
  data,
  fileName,
  rotation,
  zoom,
}: {
  compact: boolean;
  data: Uint8Array;
  fileName: string;
  rotation: number;
  zoom: number;
}) {
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: ReturnType<PdfJsModule['getDocument']> | null = null;

    setIsLoading(true);
    setLoadError(null);
    setPdfDocument(null);

    async function loadPdfDocument() {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        if (cancelled) return;

        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        loadingTask = pdfjsLib.getDocument({ data: data.slice() });
        const loadedPdfDocument = await loadingTask.promise;

        if (cancelled) {
          void loadedPdfDocument.cleanup();
          return;
        }

        setPdfDocument(loadedPdfDocument);
        setIsLoading(false);
      } catch (error) {
        if (!cancelled) {
          setLoadError(documentViewerErrorMessage(error));
          setIsLoading(false);
        }
      }
    }

    void loadPdfDocument();

    return () => {
      cancelled = true;
      void loadingTask?.destroy();
    };
  }, [data]);

  const pageNumbers = useMemo(() => (
    pdfDocument ? Array.from({ length: pdfDocument.numPages }, (_, index) => index + 1) : []
  ), [pdfDocument]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center">
        <div>
          <i className="ri-loader-4-line animate-spin text-3xl text-primary" aria-hidden />
          <p className="mt-3 font-instrument text-sm text-textSecondary">Renderizando PDF...</p>
        </div>
      </div>
    );
  }

  if (loadError || !pdfDocument) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-center">
        <div>
          <i className="ri-file-warning-line text-3xl text-amber-600" aria-hidden />
          <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">Não foi possível renderizar o PDF</p>
          <p className="mt-1 font-instrument text-xs text-textSecondary">{loadError ?? 'Documento indisponível.'}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      aria-label={`Preview de ${fileName}`}
      className={classNames(
        'flex flex-1 items-start justify-center overflow-auto bg-muted p-4',
        compact ? 'max-h-[760px]' : ''
      )}
    >
      <div className="flex min-w-max flex-col items-center gap-4">
        {pageNumbers.map((pageNumber) => (
          <PdfPageCanvas
            key={`${pageNumber}-${zoom}-${rotation}`}
            pageNumber={pageNumber}
            pdfDocument={pdfDocument}
            rotation={rotation}
            zoom={zoom}
          />
        ))}
      </div>
    </div>
  );
}

function DocumentPreview({
  compact = false,
  documento,
  onRetry,
  preview,
}: {
  compact?: boolean;
  documento: DocumentoView;
  onRetry: () => void;
  preview?: DocumentPreviewState;
}) {
  const hasPreview = Boolean(preview);
  const isLoading = preview?.loading ?? false;
  const canDownload = Boolean(preview?.url);
  const [viewerTransform, setViewerTransform] = useState<DocumentViewerTransform>(DEFAULT_DOCUMENT_VIEWER_TRANSFORM);
  const isTransformablePreview = Boolean(preview?.url && (preview.kind === 'pdf' || preview.kind === 'image'));
  const canZoomOut = viewerTransform.zoom > DOCUMENT_ZOOM_MIN;
  const canZoomIn = viewerTransform.zoom < DOCUMENT_ZOOM_MAX;

  useEffect(() => {
    setViewerTransform(DEFAULT_DOCUMENT_VIEWER_TRANSFORM);
  }, [documento.id, preview?.url, preview?.kind]);

  const updateViewerTransform = useCallback((updates: Partial<DocumentViewerTransform>) => {
    setViewerTransform((current) => ({
      rotation: normalizeDocumentRotation(updates.rotation ?? current.rotation),
      zoom: clampDocumentZoom(updates.zoom ?? current.zoom),
    }));
  }, []);

  return (
    <div
      className={classNames(
        'flex flex-col rounded-lg border border-dashed border-border-subtle bg-muted',
        compact ? 'min-h-[560px] p-3' : 'min-h-[320px] p-4'
      )}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-instrument text-[11px] text-textSecondary">
            {documento.paginas} página{documento.paginas > 1 ? 's' : ''} · OCR {documento.confianca}%
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {isTransformablePreview && (
            <>
              <DocumentToolbarIconButton
                disabled={!canZoomOut}
                icon="ri-zoom-out-line"
                onClick={() => updateViewerTransform({ zoom: viewerTransform.zoom - DOCUMENT_ZOOM_STEP })}
                title="Diminuir zoom"
              />
              <DocumentToolbarIconButton
                disabled={!canZoomIn}
                icon="ri-zoom-in-line"
                onClick={() => updateViewerTransform({ zoom: viewerTransform.zoom + DOCUMENT_ZOOM_STEP })}
                title="Aumentar zoom"
              />
              <DocumentToolbarIconButton
                icon="ri-anticlockwise-line"
                onClick={() => updateViewerTransform({ rotation: viewerTransform.rotation - 90 })}
                title="Rotacionar para esquerda"
              />
              <DocumentToolbarIconButton
                icon="ri-clockwise-line"
                onClick={() => updateViewerTransform({ rotation: viewerTransform.rotation + 90 })}
                title="Rotacionar para direita"
              />
            </>
          )}
          {preview?.url && (
            <a
              href={preview.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 font-instrument text-xs font-medium text-textSecondary transition-colors hover:bg-card hover:text-textPrimary"
              title="Abrir documento"
            >
              <i className="ri-external-link-line text-base" aria-hidden />
              Abrir
            </a>
          )}
          {canDownload && (
            <a
              href={preview?.url ?? undefined}
              download={preview?.fileName ?? fileNameFromDocumento(documento)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-card hover:text-textPrimary"
              title="Baixar documento"
            >
              <i className="ri-download-line text-base" aria-hidden />
            </a>
          )}
          {preview?.error && (
            <HabitaButton
              icon="ri-refresh-line"
              onClick={onRetry}
              size="icon"
              title="Tentar novamente"
              variant="ghost"
            />
          )}
        </div>
      </div>
      <div
        className={classNames(
          'flex flex-1 overflow-hidden rounded-md bg-card',
          compact ? 'mt-3 min-h-[520px] max-h-[760px]' : 'mt-4 min-h-[360px]'
        )}
      >
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <div>
              <i className="ri-loader-4-line animate-spin text-3xl text-primary" aria-hidden />
              <p className="mt-3 font-instrument text-sm text-textSecondary">Carregando documento...</p>
            </div>
          </div>
        ) : preview?.error ? (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <div>
              <i className="ri-file-warning-line text-3xl text-amber-600" aria-hidden />
              <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">Não foi possível abrir o preview</p>
              <p className="mt-1 font-instrument text-xs text-textSecondary">{preview.error}</p>
              <HabitaButton className="mt-4" icon="ri-refresh-line" onClick={onRetry} size="sm">
                Tentar novamente
              </HabitaButton>
            </div>
          </div>
        ) : !hasPreview ? (
          <div className="flex flex-1 items-center justify-center p-6" aria-hidden />
        ) : preview?.data && preview.kind === 'pdf' ? (
          <DocumentPdfViewer
            compact={compact}
            data={preview.data}
            fileName={preview.fileName}
            rotation={viewerTransform.rotation}
            zoom={viewerTransform.zoom}
          />
        ) : preview?.url && preview.kind === 'image' ? (
          <DocumentImageViewer
            alt={documento.nome}
            compact={compact}
            rotation={viewerTransform.rotation}
            src={preview.url}
            zoom={viewerTransform.zoom}
          />
        ) : (
          <div className="flex flex-1 items-center justify-center p-6 text-center">
            <div>
              <i className="ri-file-download-line text-3xl text-textSecondary" aria-hidden />
              <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">Preview indisponível para este formato</p>
              <p className="mt-1 font-instrument text-xs text-textSecondary">
                Baixe o arquivo para visualizar no aplicativo apropriado.
              </p>
              {canDownload && (
                <a
                  href={preview?.url ?? undefined}
                  download={preview?.fileName ?? fileNameFromDocumento(documento)}
                  className={`mt-4 inline-flex h-8 items-center gap-2 rounded-md border ${habitaControlBorderClass} bg-card px-3 font-instrument text-xs font-medium text-textPrimary transition-colors hover:bg-accent`}
                >
                  <i className="ri-download-line text-base" aria-hidden />
                  Baixar documento
                </a>
              )}
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

function PastaDownloadDialog({
  documentos,
  isBuildingZip,
  isZipCancelRequested,
  isOpen,
  onClose,
  onDownload,
  onPreviewDocument,
  onRetryPreview,
  onToggleAll,
  onToggleDocument,
  previewByDocId,
  previewDocId,
  selectedDocIds,
  zipProgress,
}: {
  documentos: DocumentoView[];
  isBuildingZip: boolean;
  isZipCancelRequested: boolean;
  isOpen: boolean;
  onClose: () => void;
  onDownload: () => void;
  onPreviewDocument: (docId: string) => void;
  onRetryPreview: (documento: DocumentoView) => void;
  onToggleAll: () => void;
  onToggleDocument: (docId: string) => void;
  previewByDocId: Record<string, DocumentPreviewState>;
  previewDocId: string | null;
  selectedDocIds: string[];
  zipProgress: ZipProgressState;
}) {
  const activeDocument = documentos.find((documento) => documento.id === previewDocId) ?? documentos[0] ?? null;
  const allSelected = documentos.length > 0 && selectedDocIds.length === documentos.length;

  return (
    <HabitaDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Baixar pasta"
      description="Selecione os documentos prontos para incluir no ZIP."
      size="xl"
      footer={
        <div className="flex w-full flex-wrap justify-end gap-3 pb-2 pt-1">
          <HabitaButton onClick={onClose} variant="ghost">
            {isBuildingZip ? 'Cancelar geração' : 'Cancelar'}
          </HabitaButton>
          <HabitaButton
            disabled={selectedDocIds.length === 0 || isBuildingZip}
            icon={isBuildingZip ? 'ri-loader-4-line' : 'ri-file-zip-line'}
            onClick={onDownload}
          >
            {isBuildingZip ? 'Gerando ZIP...' : `Baixar ${selectedDocIds.length} selecionado${selectedDocIds.length === 1 ? '' : 's'}`}
          </HabitaButton>
        </div>
      }
    >
      {documentos.length === 0 ? (
        <div className="rounded-md border border-dashed border-border-subtle p-6 text-center">
          <i className="ri-file-warning-line text-3xl text-textSecondary" aria-hidden />
          <p className="mt-3 font-instrument text-sm font-semibold text-textPrimary">
            Nenhum documento pronto para download
          </p>
          <p className="mt-1 font-instrument text-xs text-textSecondary">
            O ZIP fica disponível quando houver documentos validados na pasta.
          </p>
        </div>
      ) : (
        <div className="space-y-3" aria-busy={isBuildingZip}>
          {isBuildingZip && (
            <div className="flex items-start gap-3 rounded-md border border-primary/25 bg-primary/10 p-3">
              <i className="ri-loader-4-line mt-0.5 shrink-0 animate-spin text-lg text-primary" aria-hidden />
              <div className="min-w-0">
                <p className="font-instrument text-sm font-semibold text-textPrimary">
                  {isZipCancelRequested ? 'Cancelando geração do ZIP...' : 'Gerando ZIP...'}
                </p>
                <p className="mt-0.5 whitespace-normal break-words font-instrument text-xs text-textSecondary">
                  {zipProgress
                    ? `${zipProgress.current} de ${zipProgress.total} · ${zipProgress.label}`
                    : 'Preparando documentos selecionados.'}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(260px,340px)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <p className="font-instrument text-xs font-medium text-textSecondary">
                  {selectedDocIds.length} de {documentos.length} documento{documentos.length === 1 ? '' : 's'}
                </p>
                <HabitaButton icon={allSelected ? 'ri-checkbox-blank-line' : 'ri-checkbox-multiple-line'} onClick={onToggleAll} size="sm" variant="ghost">
                  {allSelected ? 'Limpar' : 'Todos'}
                </HabitaButton>
              </div>

              <div className="max-h-[620px] space-y-2 overflow-y-auto pr-1">
                {documentos.map((documento) => {
                  const isSelected = selectedDocIds.includes(documento.id);
                  const isActive = activeDocument?.id === documento.id;

                  return (
                    <div
                      key={documento.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => onPreviewDocument(documento.id)}
                      onKeyDown={(event) => {
                        if (event.target instanceof HTMLInputElement) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onPreviewDocument(documento.id);
                        }
                      }}
                      className={classNames(
                        'rounded-lg border bg-card transition-colors',
                        isActive ? 'border-primary shadow-sm' : 'border-border-subtle hover:border-primary/50'
                      )}
                    >
                      <div className="flex items-center gap-3 px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={`Selecionar ${documento.nome}`}
                          checked={isSelected}
                          onClick={(event) => event.stopPropagation()}
                          onChange={() => onToggleDocument(documento.id)}
                          className="h-4 w-4 shrink-0 rounded border-border-subtle accent-primary"
                        />
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                          <i className={`${documentoIconClassName(documento)} text-base`} aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-instrument text-sm font-semibold text-textPrimary">
                            {documento.nome}
                          </span>
                          <span className="block font-instrument text-[11px] text-textSecondary">
                            ID {documento.id} · {formatHabitaDateTime(documento.dataEnvio)}
                          </span>
                        </span>
                        <DocumentoStatusBadge status={documento.status} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="min-w-0">
              {activeDocument ? (
                <DocumentPreview
                  compact
                  documento={activeDocument}
                  onRetry={() => onRetryPreview(activeDocument)}
                  preview={previewByDocId[activeDocument.id]}
                />
              ) : (
                <div className="flex min-h-[560px] items-center justify-center rounded-lg border border-dashed border-border-subtle bg-muted p-6 text-center">
                  <p className="font-instrument text-sm text-textSecondary">Selecione um documento para visualizar.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </HabitaDialog>
  );
}

function createInitialPreviewState(documento: DocumentoView): DocumentPreviewState {
  const fileName = fileNameFromDocumento(documento);
  const mimeType = fallbackMimeType(documento);

  return {
    data: null,
    error: null,
    fileName,
    kind: previewKindFromMimeType(mimeType),
    loading: true,
    mimeType,
    url: null,
  };
}

function createLoadedPreviewState(
  documento: DocumentoView,
  data: ArrayBuffer,
  responseMimeType: string | undefined
): DocumentPreviewState {
  const fileName = fileNameFromDocumento(documento);
  const mimeType = resolvePreviewMimeType({
    data,
    mimeType: responseMimeType,
    names: [documento.arquivoNomeOriginal, documento.path, documento.nome],
  }) ?? fallbackMimeType(documento);
  const previewData = new Uint8Array(data.slice(0));
  const blob = new Blob([data], { type: mimeType });

  return {
    data: previewData,
    error: null,
    fileName,
    kind: previewKindFromMimeType(mimeType),
    loading: false,
    mimeType,
    url: URL.createObjectURL(blob),
  };
}

function createErrorPreviewState(documento: DocumentoView, error?: unknown): DocumentPreviewState {
  const fileName = fileNameFromDocumento(documento);
  const mimeType = fallbackMimeType(documento);
  const message = sanitizeFileAccessErrorMessage(error);

  return {
    data: null,
    error: message,
    fileName,
    kind: previewKindFromMimeType(mimeType),
    loading: false,
    mimeType,
    url: null,
  };
}

function optionalDisplayString(value: unknown): string | undefined {
  if (value === null || value === undefined || String(value).trim() === '') return undefined;
  return String(value);
}

function sourceOriginLabel(value: string | null | undefined, fallback: HabitaCampoExtraido['origem']): HabitaCampoExtraido['origem'] {
  const normalized = value?.trim().toLowerCase();
  if (normalized === 'ocr') return 'OCR';
  if (normalized === 'manual') return 'Manual';
  if (normalized === 'ia') return 'IA';
  if (normalized === 'chavinha') return 'Chavinha';
  return fallback;
}

function isDisplayValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '' && String(value).trim() !== '-';
}

function fieldStatusFromSource(source: NonNullable<HabitaCliente['fieldSources']>[string]): CampoExtraidoStatus {
  if (source.confidence === null || source.confidence === undefined) return 'sem_validacao';
  const confidence = source.confidence >= 0 && source.confidence <= 1
    ? source.confidence * 100
    : source.confidence;
  if (confidence <= 0) return 'sem_validacao';
  return confidence < 75 ? 'revisao' : 'validado';
}

function fromDossierSource(cliente: HabitaCliente, sourceKey: string, item: ResumoItem): ResumoItem | null {
  const source = cliente.fieldSources?.[sourceKey];
  if (!source || !isDisplayValue(item.valor)) return null;

  const fileId = optionalDisplayString(source.source_detail?.file_id);
  const sourceName = optionalDisplayString(source.source_detail?.source_name);

  return {
    ...item,
    origem: sourceOriginLabel(source.source, item.origem),
    documento: sourceName ?? item.documento,
    docId: fileId ?? item.docId,
    sourceDetail: source.source_detail,
    status: fieldStatusFromSource(source),
  };
}

function buildParentageResumo(cliente: HabitaCliente, docId?: string): Array<ResumoItem | null> {
  const parents = cliente.pais ?? [];

  return parents.slice(0, 2).map((parentName, index) => (
    fromDossierSource(cliente, 'parents', {
      label: `Nome do genitor ${index + 1}`,
      valor: parentName,
      origem: 'OCR',
      documento: 'Identificação',
      docId,
      status: 'validado',
    })
  ));
}

function buildResumo(cliente: HabitaCliente) {
  const docByCategoria = (categoria: HabitaDocumento['categoria']) =>
    cliente.documentos.find((documento) => documento.categoria === categoria);
  const identificacao = docByCategoria('identificacao');
  const residencia = docByCategoria('residencia');
  const renda = docByCategoria('renda');

  return [
    fromDossierSource(cliente, 'full_name', { label: 'Nome', valor: cliente.nomeCompleto ?? cliente.nome, origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
    fromDossierSource(cliente, 'cpf', { label: 'CPF', valor: cliente.cpf, origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
    fromDossierSource(cliente, 'gender', { label: 'Gênero', valor: cliente.genero ?? '-', origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
    ...buildParentageResumo(cliente, identificacao?.id),
    fromDossierSource(cliente, 'residence_data', { label: 'CEP', valor: cliente.cep, origem: 'OCR', documento: 'Residência', docId: residencia?.id, status: 'validado' }),
    fromDossierSource(cliente, 'residence_data', { label: 'Endereço', valor: cliente.endereco, origem: 'OCR', documento: 'Residência', docId: residencia?.id, status: 'validado' }),
    fromDossierSource(cliente, 'income_data', { label: 'Renda', valor: cliente.renda, origem: 'Chavinha', documento: 'Renda', docId: renda?.id, status: 'validado' }),
    fromDossierSource(cliente, 'income_data', { label: 'Renda líquida', valor: cliente.rendaLiquida ?? '-', origem: 'Chavinha', documento: 'Renda', docId: renda?.id, status: 'validado' }),
    fromDossierSource(cliente, 'income_data', { label: 'Profissão', valor: cliente.profissao, origem: 'OCR', documento: 'Renda', docId: renda?.id, status: 'validado' }),
    fromDossierSource(cliente, 'civil_state_data', { label: 'Estado civil', valor: cliente.estadoCivil, origem: 'OCR', documento: 'Estado civil', status: 'validado' }),
    fromDossierSource(cliente, 'civil_state_data', { label: 'Regime de bens', valor: cliente.regimeBens ?? '-', origem: 'OCR', documento: 'Estado civil', status: 'validado' }),
    fromDossierSource(cliente, 'birth_date', { label: 'Nascimento', valor: cliente.nascimento, origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
    fromDossierSource(cliente, 'birth_place', { label: 'Naturalidade', valor: cliente.localNascimento ?? '-', origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
    fromDossierSource(cliente, 'nationality', { label: 'Nacionalidade', valor: cliente.nacionalidade ?? '-', origem: 'OCR', documento: 'Identificação', docId: identificacao?.id, status: 'validado' }),
  ].filter((item): item is ResumoItem => item !== null);
}


export function PastaDetalheExperience({
  canManagePreAnalysisActions,
  detail,
  backTo,
  backLabel,
  onLoadDocumentPreview,
  onLoadPendingItems,
  onLoadProcessFiles,
  onRegisterPreAnalysisResult,
  onRequestDocumentAdjustment,
  onValidateFolder,
  canValidateFolder: canValidateFolderAction,
  statusHistory,
  validationReviewerLabel,
}: {
  canManagePreAnalysisActions: boolean;
  canValidateFolder: boolean;
  detail: HabitaProcessDetail;
  backTo: string;
  backLabel: string;
  onLoadDocumentPreview: DocumentPreviewLoader;
  onLoadPendingItems: PendingItemsLoader;
  onLoadProcessFiles: ProcessFilesLoader;
  onRegisterPreAnalysisResult: PreAnalysisStatusTransitioner;
  onRequestDocumentAdjustment: DocumentAdjustmentRequester;
  onValidateFolder: FolderValidator;
  statusHistory: HabitaProcessStatusHistoryEntry[];
  validationReviewerLabel: string;
}) {
  const detailId = String(detail.id);
  const [hydratedFiles, setHydratedFiles] = useState<ProcessFileModel[]>([]);
  const [hydratedFilesProcessId, setHydratedFilesProcessId] = useState<string | null>(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const activeHydratedFiles = hydratedFilesProcessId === detailId ? hydratedFiles : EMPTY_PROCESS_FILES;
  const cliente = useMemo(() => mapProcessDetailToHabitaCliente(detail, activeHydratedFiles), [activeHydratedFiles, detail]);
  const [statusOverride, setStatusOverride] = useState<PastaStatus | null>(null);
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, FieldOverride>>({});
  const [documentStatusOverrides, setDocumentStatusOverrides] = useState<Record<string, DocumentoStatus>>({});
  const [activeDocId, setActiveDocId] = useState('');
  const [reviewing, setReviewing] = useState<ReviewingState>(null);
  const [editValue, setEditValue] = useState('');
  const [editNota, setEditNota] = useState('');
  const [pendingItems, setPendingItems] = useState<HabitaPendingItems | null>(null);
  const [pendingItemsProcessId, setPendingItemsProcessId] = useState<string | null>(null);
  const [pendingItemsLoading, setPendingItemsLoading] = useState(false);
  const [pendingItemsError, setPendingItemsError] = useState<string | null>(null);
  const [ajusteOpen, setAjusteOpen] = useState(false);
  const [ajusteDocIds, setAjusteDocIds] = useState<string[]>([]);
  const [ajusteJustificativa, setAjusteJustificativa] = useState('');
  const [isSubmittingAjuste, setIsSubmittingAjuste] = useState(false);
  const [validarOpen, setValidarOpen] = useState(false);
  const [isSubmittingValidacao, setIsSubmittingValidacao] = useState(false);
  const [preAnalysisDecision, setPreAnalysisDecision] = useState<PreAnalysisDecision | null>(null);
  const [preAnalysisJustification, setPreAnalysisJustification] = useState('');
  const [preAnalysisAttachments, setPreAnalysisAttachments] = useState<PreAnalysisDraftAttachment[]>([]);
  const [isSubmittingPreAnalysis, setIsSubmittingPreAnalysis] = useState(false);
  const [confirmCorrectionOpen, setConfirmCorrectionOpen] = useState(false);
  const [isSubmittingCorrection, setIsSubmittingCorrection] = useState(false);
  const [validationTimelineEvents, setValidationTimelineEvents] = useState<DetailTimelineEvent[]>([]);
  const [actionMessage, setActionMessage] = useState<ActionMessageState>(null);
  const [previewByDocId, setPreviewByDocId] = useState<Record<string, DocumentPreviewState>>({});
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadDocIds, setDownloadDocIds] = useState<string[]>([]);
  const [downloadPreviewDocId, setDownloadPreviewDocId] = useState<string | null>(null);
  const [isBuildingZip, setIsBuildingZip] = useState(false);
  const [isZipCancelRequested, setIsZipCancelRequested] = useState(false);
  const [zipProgress, setZipProgress] = useState<ZipProgressState>(null);
  const [showHiddenDocumentosByGroup, setShowHiddenDocumentosByGroup] = useState<Record<string, boolean>>({});
  const [expandedDocumentoGroupId, setExpandedDocumentoGroupId] = useState<string | null>(null);
  const [resumoCollapsed, setResumoCollapsed] = useState(false);
  const actionMessageTimerRef = useRef<number | null>(null);
  const previewUrlsRef = useRef(new Set<string>());
  const preAnalysisAttachmentUrlsRef = useRef(new Set<string>());
  const previewByDocIdRef = useRef(previewByDocId);
  const zipGenerationCancelledRef = useRef(false);
  const pendingItemsRequestSequenceRef = useRef(0);
  const previousClienteRef = useRef({ id: cliente.id, status: cliente.status });

  const pastaStatusAtual = statusOverride ?? cliente.status;
  const isNovo = pastaStatusAtual === 'NOVO';
  const canRegisterPreAnalysisResult = canManagePreAnalysisActions && pastaStatusAtual === 'PRE_ANALISE_CREDITO';
  const canCorrectPreAnalysisResult = canManagePreAnalysisActions && preAnalysisResultStatuses.has(pastaStatusAtual);
  const canRequestDocumentAdjustment = canManagePreAnalysisActions && pastaStatusAtual === 'PRE_ANALISE_CREDITO';
  const canValidateFolder = canValidateFolderAction && pastaStatusAtual === 'VALIDACAO_PASTA';
  const activePreAnalysisConfig = preAnalysisDecision ? preAnalysisDecisionConfigs[preAnalysisDecision] : null;
  const resumo = useMemo(() => buildResumo(cliente), [cliente]);
  const contactUrl = useMemo(() => buildContactUrl(cliente), [cliente]);
  const chatwootUrl = useMemo(() => buildChatwootContactUrl(cliente), [cliente]);

  useEffect(() => {
    previewByDocIdRef.current = previewByDocId;
  }, [previewByDocId]);

  useEffect(() => {
    setHydratedFiles([]);
    setHydratedFilesProcessId(null);
    setFilesLoading(false);
    setFilesError(null);
  }, [detailId]);

  useEffect(() => {
    const previousCliente = previousClienteRef.current;

    if (previousCliente.id !== cliente.id) {
      setStatusOverride(null);
      setFieldOverrides({});
      setDocumentStatusOverrides({});
      setActiveDocId('');
      setReviewing(null);
      setEditValue('');
      setEditNota('');
      setPendingItems(null);
      setPendingItemsProcessId(null);
      setPendingItemsLoading(false);
      setPendingItemsError(null);
      setAjusteOpen(false);
      setAjusteDocIds([]);
      setAjusteJustificativa('');
      setIsSubmittingAjuste(false);
      setValidarOpen(false);
      setIsSubmittingValidacao(false);
      setPreAnalysisDecision(null);
      setPreAnalysisJustification('');
      setPreAnalysisAttachments([]);
      setIsSubmittingPreAnalysis(false);
      setConfirmCorrectionOpen(false);
      setIsSubmittingCorrection(false);
      setValidationTimelineEvents([]);
      setActionMessage(null);
      setDownloadOpen(false);
      setDownloadDocIds([]);
      setDownloadPreviewDocId(null);
      setIsBuildingZip(false);
      setIsZipCancelRequested(false);
      setZipProgress(null);
      zipGenerationCancelledRef.current = true;
      setShowHiddenDocumentosByGroup({});
      setExpandedDocumentoGroupId(null);
      setPreviewByDocId((current) => {
        Object.values(current).forEach((preview) => {
          if (preview.url) {
            URL.revokeObjectURL(preview.url);
            previewUrlsRef.current.delete(preview.url);
          }
        });
        previewByDocIdRef.current = {};
        return {};
      });
      preAnalysisAttachmentUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      preAnalysisAttachmentUrlsRef.current.clear();
      if (actionMessageTimerRef.current) {
        window.clearTimeout(actionMessageTimerRef.current);
        actionMessageTimerRef.current = null;
      }
    } else if (previousCliente.status !== cliente.status) {
      setStatusOverride(null);
    }

    previousClienteRef.current = { id: cliente.id, status: cliente.status };
  }, [cliente.id, cliente.status]);

  const documentos = useMemo<DocumentoView[]>(() => {
    if (isNovo) return [];

    return cliente.documentos.map((documento) => ({
      ...documento,
      status: documentStatusOverrides[documento.id] ?? documento.status,
      campos: documento.campos.map((campo) => {
        const override = fieldOverrides[fieldKey(documento.id, campo.label)];
        if (!override) return campo;
        return {
          ...campo,
          valor: override.valor,
          status: override.status,
          override,
        };
      }),
    }));
  }, [cliente.documentos, documentStatusOverrides, fieldOverrides, isNovo]);

  const downloadableDocumentos = useMemo(
    () => documentos.filter((documento) => documento.status === 'validado'),
    [documentos]
  );

  const ajustesDocumentais = useMemo(
    () => buildAjustesDocumentaisFromPendingItems(pendingItems, documentos, cliente.id),
    [cliente.id, documentos, pendingItems]
  );

  const ajustesEmAberto = useMemo(() => (
    new Set(
      ajustesDocumentais
        .filter((ajuste) => ajuste.status === 'aguardando')
        .map((ajuste) => ajuste.docId)
    )
  ), [ajustesDocumentais]);

  const documentoGroups = useMemo(
    () => buildDocumentoGroups(documentos, showHiddenDocumentosByGroup),
    [documentos, showHiddenDocumentosByGroup]
  );
  const validationDocumentSummary = useMemo(
    () => buildValidationDocumentSummary(documentos),
    [documentos]
  );
  const statusHistoryTimelineEvents = useMemo(
    () => buildStatusHistoryTimeline(statusHistory),
    [statusHistory]
  );
  const hasPersistedValidationEvent = useMemo(
    () => statusHistoryTimelineEvents.some((event) => event.descricao === 'Pasta validada'),
    [statusHistoryTimelineEvents]
  );
  const timelineEvents = useMemo<DetailTimelineEvent[]>(
    () => [
      ...(hasPersistedValidationEvent ? [] : validationTimelineEvents),
      ...statusHistoryTimelineEvents,
      ...cliente.timeline,
    ],
    [cliente.timeline, hasPersistedValidationEvent, statusHistoryTimelineEvents, validationTimelineEvents]
  );
  const currentPreAnalysisDecision = preAnalysisDecisionByStatus[pastaStatusAtual] ?? null;
  const currentPreAnalysisConfig = currentPreAnalysisDecision
    ? preAnalysisDecisionConfigs[currentPreAnalysisDecision]
    : null;
  const currentPreAnalysisEvent = useMemo(() => {
    if (!currentPreAnalysisConfig) return null;

    return [...timelineEvents]
      .sort((left, right) => timestampOf(right.data) - timestampOf(left.data))
      .find((event) => event.descricao === currentPreAnalysisConfig.eventDescription) ?? null;
  }, [currentPreAnalysisConfig, timelineEvents]);

  const visibleDocumentos = useMemo(
    () => documentoGroups.flatMap((group) => group.visibleDocumentos),
    [documentoGroups]
  );

  useEffect(() => {
    if (visibleDocumentos.length === 0) {
      setActiveDocId('');
      return;
    }

    if (!visibleDocumentos.some((documento) => documento.id === activeDocId)) {
      setActiveDocId(visibleDocumentos[0].id);
    }
  }, [activeDocId, visibleDocumentos]);

  const expandedDocumentoGroup = useMemo(
    () => documentoGroups.find((group) => group.id === expandedDocumentoGroupId) ?? null,
    [documentoGroups, expandedDocumentoGroupId]
  );
  const expandedActiveDoc = expandedDocumentoGroup
    ? expandedDocumentoGroup.visibleDocumentos.find((documento) => documento.id === activeDocId) ??
      expandedDocumentoGroup.visibleDocumentos[0]
    : null;
  const reviewingDoc = reviewing
    ? documentos.find((documento) => documento.id === reviewing.docId) ?? null
    : null;

  useEffect(() => {
    const availableIds = new Set(downloadableDocumentos.map((documento) => documento.id));

    setDownloadDocIds((current) => current.filter((docId) => availableIds.has(docId)));
    setDownloadPreviewDocId((current) => (
      current && availableIds.has(current)
        ? current
        : downloadableDocumentos[0]?.id ?? null
    ));
  }, [downloadableDocumentos]);

  const ensureProcessFiles = useCallback(async ({ force = false }: { force?: boolean } = {}) => {
    if ((!force && hydratedFilesProcessId === cliente.id) || filesLoading || (!force && filesError)) return;

    setFilesLoading(true);
    setFilesError(null);

    try {
      const files = await onLoadProcessFiles(cliente.id);
      setHydratedFiles(files);
      setHydratedFilesProcessId(cliente.id);
    } catch (error) {
      console.error('Erro ao carregar arquivos do processo:', error);
      setFilesError('Não foi possível carregar os dados extraídos dos documentos.');
    } finally {
      setFilesLoading(false);
    }
  }, [cliente.id, filesError, filesLoading, hydratedFilesProcessId, onLoadProcessFiles]);

  useEffect(() => {
    void ensureProcessFiles();
  }, [ensureProcessFiles]);

  const loadPendingItems = useCallback(async ({
    force = false,
    silent = false,
  }: { force?: boolean; silent?: boolean } = {}) => {
    if ((!force && pendingItemsProcessId === cliente.id) || (!force && pendingItemsLoading)) return;

    const requestId = pendingItemsRequestSequenceRef.current + 1;
    pendingItemsRequestSequenceRef.current = requestId;

    if (!silent) {
      setPendingItemsLoading(true);
    }
    setPendingItemsError(null);

    try {
      const items = await onLoadPendingItems(cliente.id);
      if (pendingItemsRequestSequenceRef.current !== requestId) return;

      setPendingItems(items);
      setPendingItemsProcessId(cliente.id);
    } catch (error) {
      console.error('Erro ao carregar ajustes documentais:', error);
      if (pendingItemsRequestSequenceRef.current !== requestId) return;

      setPendingItems(null);
      setPendingItemsProcessId(cliente.id);
      setPendingItemsError('Não foi possível carregar os ajustes documentais.');
    } finally {
      if (pendingItemsRequestSequenceRef.current === requestId) {
        setPendingItemsLoading(false);
      }
    }
  }, [cliente.id, onLoadPendingItems, pendingItemsLoading, pendingItemsProcessId]);

  useEffect(() => {
    void loadPendingItems();
  }, [loadPendingItems]);

  const revokePreviewUrl = useCallback((url: string | null | undefined) => {
    if (!url) return;
    URL.revokeObjectURL(url);
    previewUrlsRef.current.delete(url);
  }, []);

  const loadDocumentPreview = useCallback(async (documento: DocumentoView, { force = false }: { force?: boolean } = {}) => {
    const currentPreview = previewByDocIdRef.current[documento.id];
    if (!force && (currentPreview?.loading || currentPreview?.url || currentPreview?.error)) return;

    if (force) {
      revokePreviewUrl(currentPreview?.url);
    }

    setPreviewByDocId((current) => {
      const next = {
        ...current,
        [documento.id]: createInitialPreviewState(documento),
      };
      previewByDocIdRef.current = next;
      return next;
    });

    try {
      const result = await onLoadDocumentPreview(documento);
      const loadedPreview = createLoadedPreviewState(documento, result.data, result.mimeType);

      if (loadedPreview.url) {
        previewUrlsRef.current.add(loadedPreview.url);
      }
      setPreviewByDocId((current) => {
        revokePreviewUrl(current[documento.id]?.url);
        const next = {
          ...current,
          [documento.id]: loadedPreview,
        };
        previewByDocIdRef.current = next;
        return next;
      });
    } catch (error) {
      console.error('Erro ao carregar preview do documento:', error);
      setPreviewByDocId((current) => {
        revokePreviewUrl(current[documento.id]?.url);
        const next = {
          ...current,
          [documento.id]: createErrorPreviewState(documento, error),
        };
        previewByDocIdRef.current = next;
        return next;
      });
    }
  }, [onLoadDocumentPreview, revokePreviewUrl]);

  useEffect(() => {
    if (expandedActiveDoc) {
      void loadDocumentPreview(expandedActiveDoc);
    }
  }, [expandedActiveDoc, loadDocumentPreview]);

  useEffect(() => {
    if (reviewingDoc) {
      void loadDocumentPreview(reviewingDoc);
    }
  }, [loadDocumentPreview, reviewingDoc]);

  useEffect(() => {
    if (!downloadOpen || !downloadPreviewDocId) return;

    const previewDocument = downloadableDocumentos.find((documento) => documento.id === downloadPreviewDocId);
    if (previewDocument) {
      void loadDocumentPreview(previewDocument);
    }
  }, [downloadOpen, downloadableDocumentos, downloadPreviewDocId, loadDocumentPreview]);

  useEffect(() => {
    return () => {
      zipGenerationCancelledRef.current = true;
      if (actionMessageTimerRef.current) {
        window.clearTimeout(actionMessageTimerRef.current);
      }
      previewUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrlsRef.current.clear();
      preAnalysisAttachmentUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      preAnalysisAttachmentUrlsRef.current.clear();
    };
  }, []);

  function showMessage(
    message: string,
    {
      duration = 2500,
      variant = 'success',
    }: { duration?: number; variant?: ActionMessageVariant } = {}
  ) {
    if (actionMessageTimerRef.current) {
      window.clearTimeout(actionMessageTimerRef.current);
    }

    setActionMessage({ message, variant });
    actionMessageTimerRef.current = window.setTimeout(() => {
      setActionMessage(null);
      actionMessageTimerRef.current = null;
    }, duration);
  }

  async function loadDocumentDataForZip(documento: DocumentoView): Promise<{ data: ArrayBuffer | Uint8Array; fileName: string }> {
    const cachedPreview = previewByDocIdRef.current[documento.id];
    if (cachedPreview?.data && !cachedPreview.error) {
      return { data: cachedPreview.data, fileName: cachedPreview.fileName };
    }

    const result = await onLoadDocumentPreview(documento);
    return { data: result.data, fileName: fileNameFromDocumento(documento) };
  }

  function openDownloadDialog() {
    if (downloadableDocumentos.length === 0) {
      showMessage('Nenhum documento pronto para baixar.');
      return;
    }

    const documentIds = downloadableDocumentos.map((documento) => documento.id);
    const firstDocument = downloadableDocumentos[0];

    setDownloadDocIds(documentIds);
    setDownloadPreviewDocId(firstDocument?.id ?? null);
    setDownloadOpen(true);

    if (firstDocument) {
      void loadDocumentPreview(firstDocument);
    }
  }

  function closeDownloadDialog() {
    if (isBuildingZip) {
      zipGenerationCancelledRef.current = true;
      setIsZipCancelRequested(true);
      setZipProgress((current) => current
        ? { ...current, label: 'Cancelando geração...' }
        : { current: 0, label: 'Cancelando geração...', total: downloadDocIds.length }
      );
      return;
    }

    setDownloadOpen(false);
  }

  function previewDownloadDocument(docId: string) {
    const documento = downloadableDocumentos.find((currentDocumento) => currentDocumento.id === docId);

    setDownloadPreviewDocId(docId);
    if (documento) {
      void loadDocumentPreview(documento);
    }
  }

  function toggleDownloadDocument(docId: string) {
    setDownloadDocIds((current) => (
      current.includes(docId)
        ? current.filter((currentDocId) => currentDocId !== docId)
        : [...current, docId]
    ));
  }

  function toggleAllDownloadDocuments() {
    setDownloadDocIds((current) => (
      current.length === downloadableDocumentos.length
        ? []
        : downloadableDocumentos.map((documento) => documento.id)
    ));
  }

  async function downloadSelectedDocumentsZip() {
    const selectedDocuments = downloadableDocumentos.filter((documento) => downloadDocIds.includes(documento.id));

    if (selectedDocuments.length === 0) {
      showMessage('Selecione ao menos um documento para baixar.');
      return;
    }

    setIsBuildingZip(true);
    setIsZipCancelRequested(false);
    setZipProgress({ current: 0, label: 'Preparando documentos selecionados.', total: selectedDocuments.length });
    zipGenerationCancelledRef.current = false;

    try {
      const usedFileNames = new Set<string>();
      const entries: BrowserZipEntry[] = [];
      const failures: ZipDocumentFailure[] = [];

      for (let index = 0; index < selectedDocuments.length; index += 1) {
        if (zipGenerationCancelledRef.current) throw createZipCancelledError();

        const documento = selectedDocuments[index];
        setZipProgress({ current: index + 1, label: documento.nome, total: selectedDocuments.length });

        try {
          const file = await loadDocumentDataForZip(documento);
          if (zipGenerationCancelledRef.current) throw createZipCancelledError();

          if (file.data.byteLength === 0) {
            throw new Error('Arquivo sem conteúdo para incluir no ZIP.');
          }

          entries.push({
            data: file.data,
            name: uniqueZipEntryName(file.fileName || fileNameFromDocumento(documento), usedFileNames),
          });
        } catch (error) {
          if (isZipCancelledError(error) || zipGenerationCancelledRef.current) throw createZipCancelledError();

          console.warn('Documento ignorado na geração do ZIP:', {
            documentoId: documento.id,
            documentoNome: documento.nome,
            motivo: zipFailureReason(error),
          });
          failures.push({ documento, error });
        }
      }

      if (entries.length === 0) {
        setDownloadOpen(false);
        showMessage(
          `Nenhum arquivo foi incluído no ZIP. Arquivo(s) com problema: ${formatZipFailureMessage(failures)}`,
          { duration: 9000, variant: 'error' }
        );
        return;
      }

      if (zipGenerationCancelledRef.current) throw createZipCancelledError();
      setZipProgress({ current: entries.length, label: 'Compactando arquivos...', total: entries.length });
      const zipBlob = await createZipBlob(entries);

      if (zipGenerationCancelledRef.current) throw createZipCancelledError();

      downloadBlob(zipBlob, zipFileNameFromCliente(cliente));
      setDownloadOpen(false);
      if (failures.length > 0) {
        showMessage(
          `${entries.length} arquivo${entries.length === 1 ? '' : 's'} baixado${entries.length === 1 ? '' : 's'} em ZIP. Não incluído${failures.length === 1 ? '' : 's'}: ${formatZipFailureMessage(failures)}`,
          { duration: 9000, variant: 'warning' }
        );
      } else {
        showMessage(
          entries.length === 1
            ? 'Documento baixado em ZIP.'
            : `${entries.length} documentos baixados em ZIP.`
        );
      }
    } catch (error) {
      if (isZipCancelledError(error)) {
        setDownloadOpen(false);
        showMessage('Geração do ZIP cancelada.', { duration: 5000, variant: 'warning' });
        return;
      }

      console.error('Erro ao gerar ZIP da pasta:', error);
      showMessage('Não foi possível gerar o ZIP da pasta.', { duration: 7000, variant: 'error' });
    } finally {
      zipGenerationCancelledRef.current = false;
      setIsBuildingZip(false);
      setIsZipCancelRequested(false);
      setZipProgress(null);
    }
  }

  function openPreAnalysisDecision(decision: PreAnalysisDecision) {
    if (!canRegisterPreAnalysisResult) {
      showMessage('Você não tem permissão para registrar resultado nesta etapa.', { variant: 'warning' });
      return;
    }

    setPreAnalysisDecision(decision);
    setPreAnalysisJustification('');
    setPreAnalysisAttachments([]);
  }

  function addPreAnalysisAttachments(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);
    if (files.length === 0) return;

    setPreAnalysisAttachments((current) => [
      ...current,
      ...files.map((file) => ({
        file,
        id: localAttachmentId(),
      })),
    ]);
    event.currentTarget.value = '';
  }

  function removePreAnalysisAttachment(attachmentId: string) {
    setPreAnalysisAttachments((current) => current.filter((attachment) => attachment.id !== attachmentId));
  }

  function closePreAnalysisDecisionDialog() {
    if (isSubmittingPreAnalysis) return;
    setPreAnalysisDecision(null);
    setPreAnalysisJustification('');
    setPreAnalysisAttachments([]);
  }

  function createTimelineAttachments(attachments: PreAnalysisDraftAttachment[]): TimelineAttachment[] {
    return attachments.map(({ file, id }) => {
      const url = URL.createObjectURL(file);
      preAnalysisAttachmentUrlsRef.current.add(url);

      return {
        id,
        name: file.name,
        size: file.size,
        type: file.type,
        url,
      };
    });
  }

  async function submitPreAnalysisDecision() {
    if (!activePreAnalysisConfig || !preAnalysisDecision) return;

    if (!canRegisterPreAnalysisResult) {
      showMessage('Você não tem permissão para registrar resultado nesta etapa.', { variant: 'warning' });
      return;
    }

    const cleanJustification = preAnalysisJustification.trim();
    if (activePreAnalysisConfig.requiredJustification && !cleanJustification) {
      showMessage('Informe a justificativa para registrar este resultado.', { variant: 'warning' });
      return;
    }

    setIsSubmittingPreAnalysis(true);

    try {
      const motive = buildPreAnalysisMotive({
        attachmentNames: preAnalysisAttachments.map((attachment) => attachment.file.name),
        justification: cleanJustification,
        prefix: activePreAnalysisConfig.motivePrefix,
      });
      const eventDate = new Date().toISOString();

      await onRegisterPreAnalysisResult({
        motive,
        processId: cliente.id,
        toStatus: activePreAnalysisConfig.targetStatus,
      });

      const timelineAttachments = createTimelineAttachments(preAnalysisAttachments);

      setStatusOverride(activePreAnalysisConfig.targetStatus);
      setValidationTimelineEvents((current) => [
        {
          anexos: timelineAttachments,
          data: eventDate,
          tipo: 'aprovacao',
          descricao: activePreAnalysisConfig.eventDescription,
          usuario: validationReviewerLabel,
          justificativa: cleanJustification || undefined,
        },
        ...current,
      ]);
      setPreAnalysisDecision(null);
      setPreAnalysisJustification('');
      setPreAnalysisAttachments([]);
      showMessage(activePreAnalysisConfig.successMessage);
    } catch (error) {
      console.error('Erro ao registrar resultado da pré-análise:', error);
      const message = userFacingErrorMessage(error, 'Não foi possível registrar o resultado da pré-análise.');
      showMessage(message, { duration: 7000, variant: 'error' });
    } finally {
      setIsSubmittingPreAnalysis(false);
    }
  }

  async function submitPreAnalysisCorrection() {
    if (!canCorrectPreAnalysisResult) {
      showMessage('Você não tem permissão para corrigir o resultado nesta etapa.', { variant: 'warning' });
      return;
    }

    setIsSubmittingCorrection(true);

    try {
      const eventDate = new Date().toISOString();
      const motive = 'Resultado da pré-análise corrigido. Pasta retornada para Pré-análise.';

      await onRegisterPreAnalysisResult({
        motive,
        processId: cliente.id,
        toStatus: 'PRE_ANALISE_CREDITO',
      });

      setStatusOverride('PRE_ANALISE_CREDITO');
      setValidationTimelineEvents((current) => [
        {
          data: eventDate,
          tipo: 'aprovacao',
          descricao: 'Correção de Resultado',
          usuario: validationReviewerLabel,
          justificativa: motive,
        },
        ...current,
      ]);
      setConfirmCorrectionOpen(false);
      showMessage('Resultado corrigido. A pasta voltou para Pré-análise.');
    } catch (error) {
      console.error('Erro ao corrigir resultado da pré-análise:', error);
      const errorMessages = errorMessagesFromUnknown(error);
      const normalizedMessage = normalizeErrorText(errorMessages.join(' | '));
      const message = normalizedMessage.includes('transition_failed') || normalizedMessage.includes('transicao invalida')
        ? 'A API ainda não permite retornar este resultado para Pré-análise. É necessário liberar a transição de Aprovado/Condicional/Reprovado para Pré-análise no backend/n8n.'
        : userFacingErrorMessage(error, 'Não foi possível corrigir o resultado da pré-análise.');
      showMessage(message, { duration: 7000, variant: 'error' });
    } finally {
      setIsSubmittingCorrection(false);
    }
  }

  function openAjuste(preSelectDocId?: string) {
    if (!canRequestDocumentAdjustment) {
      showMessage('Você não tem permissão para solicitar ajuste nesta etapa.', { variant: 'warning' });
      return;
    }

    const problemDocs = documentos
      .filter((documento) => (
        (documento.status === 'reprovado' || documento.status === 'revisao') &&
        !ajustesEmAberto.has(documento.id)
      ))
      .map((documento) => documento.id);
    setAjusteDocIds(preSelectDocId ? [preSelectDocId] : problemDocs);
    setAjusteJustificativa('');
    setAjusteOpen(true);
  }

  function toggleDocumentoGroup(group: DocumentoGroup) {
    if (expandedDocumentoGroupId === group.id) {
      setExpandedDocumentoGroupId(null);
      return;
    }

    setExpandedDocumentoGroupId(group.id);
    if (!group.visibleDocumentos.some((documento) => documento.id === activeDocId)) {
      setActiveDocId(group.visibleDocumentos[0]?.id ?? '');
    }
  }

  async function submitAjuste() {
    if (!canRequestDocumentAdjustment) {
      showMessage('Você não tem permissão para solicitar ajuste nesta etapa.', { variant: 'warning' });
      return;
    }

    if (ajusteDocIds.length === 0) {
      showMessage('Selecione ao menos um documento para ajuste.', { variant: 'warning' });
      return;
    }

    if (!ajusteJustificativa.trim()) {
      showMessage('Informe a justificativa interna do ajuste.', { variant: 'warning' });
      return;
    }

    const documentosSelecionados = documentos.filter((documento) => ajusteDocIds.includes(documento.id));
    const documentosSemAjusteAberto = documentosSelecionados.filter((documento) => !ajustesEmAberto.has(documento.id));

    if (documentosSemAjusteAberto.length === 0) {
      showMessage('Já existe ajuste aberto para os documentos selecionados.', { variant: 'warning' });
      return;
    }

    const fileIds = documentosSemAjusteAberto
      .map((documento) => Number(documento.id))
      .filter((fileId) => Number.isFinite(fileId) && fileId > 0);

    if (fileIds.length === 0) {
      showMessage('Os documentos selecionados não possuem identificador válido para ajuste.', { variant: 'error' });
      return;
    }

    setIsSubmittingAjuste(true);

    try {
      const adjustmentResult = await onRequestDocumentAdjustment({
        fileIds,
        processId: cliente.id,
        rejectionReason: ajusteJustificativa.trim(),
      });

      setAjusteOpen(false);
      setAjusteDocIds([]);
      setAjusteJustificativa('');
      await loadPendingItems({ force: true, silent: true });
      void ensureProcessFiles({ force: true });

      if (adjustmentResult.retryTransitionError) {
        showMessage(adjustmentResult.retryTransitionError, { duration: 7000, variant: 'warning' });
      } else {
        showMessage(
          fileIds.length === 1
            ? 'Ajuste documental solicitado.'
            : `${fileIds.length} ajustes documentais solicitados.`
        );
      }
    } catch (error) {
      console.error('Erro ao solicitar ajuste documental:', error);
      const message = error instanceof Error && error.message.trim()
        ? error.message
        : 'Não foi possível solicitar o ajuste documental.';
      showMessage(message, { duration: 7000, variant: 'error' });
    } finally {
      setIsSubmittingAjuste(false);
    }
  }

  function openReview(docId: string, campo: HabitaCampoExtraido) {
    const override = fieldOverrides[fieldKey(docId, campo.label)];
    setReviewing({ docId, campo });
    setEditValue(override?.valor ?? campo.valor);
    setEditNota('');
  }

  function applyReview(status: CampoExtraidoStatus) {
    if (!reviewing) return;

    const key = fieldKey(reviewing.docId, reviewing.campo.label);
    setFieldOverrides((current) => ({
      ...current,
      [key]: {
        valor: editValue,
        status,
        revisor: 'Você',
        revisadoEm: now(),
        valorOriginal: reviewing.campo.valor,
        nota: editNota || undefined,
      },
    }));
    setReviewing(null);
    showMessage('Revisão de campo registrada.');
  }

  function revertOverride(docId: string, label: string) {
    setFieldOverrides((current) => {
      const next = { ...current };
      delete next[fieldKey(docId, label)];
      return next;
    });
    showMessage('Revisão revertida.');
  }

  async function confirmarValidacao() {
    setIsSubmittingValidacao(true);

    try {
      const result = await onValidateFolder(cliente.id);
      const eventDate = new Date().toISOString();

      setStatusOverride('PRE_ANALISE_CREDITO');
      setValidationTimelineEvents((current) => [
        {
          data: eventDate,
          tipo: 'validacao',
          descricao: 'Pasta validada',
          usuario: validationReviewerLabel,
          justificativa: 'Pasta validada e encaminhada para Pré-análise.',
        },
        ...current,
      ]);
      setValidarOpen(false);
      void ensureProcessFiles({ force: true });

      if (result.retryTransitionError) {
        showMessage(result.retryTransitionError, { duration: 7000, variant: 'warning' });
      } else {
        showMessage('Pasta validada e encaminhada para Pré-análise.');
      }
    } catch (error) {
      console.error('Erro ao validar pasta:', error);
      const message = error instanceof Error && error.message.trim()
        ? error.message
        : 'Não foi possível validar a pasta.';
      showMessage(message, { duration: 7000, variant: 'error' });
    } finally {
      setIsSubmittingValidacao(false);
    }
  }

  return (
    <section className="w-full space-y-4">
      {actionMessage && (
        <div className={classNames('whitespace-normal break-words rounded-lg border px-4 py-3 font-instrument text-sm', actionMessageVariantClasses[actionMessage.variant])}>
          {actionMessage.message}
        </div>
      )}

      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 gap-3">
          <Link
            to={backTo}
            className="inline-flex h-9 shrink-0 items-center gap-2 rounded-md px-2 font-instrument text-sm text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary"
          >
            <i className="ri-arrow-left-line text-lg" aria-hidden />
            Voltar para {backLabel}
          </Link>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="truncate font-fraunces text-xl font-semibold text-textPrimary">{cliente.nome}</h1>
              <PastaStatusBadge status={pastaStatusAtual} />
            </div>
            <p className="mt-1 font-instrument text-xs text-textSecondary">
              ID do processo {cliente.processoId} · CPF {cliente.cpf} · Responsável {cliente.responsavel}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 xl:justify-end">
          <ExternalUrlAction
            disabledTitle="Contato indisponível"
            icon="ri-user-add-line"
            size="sm"
            title="Abrir contato"
            url={contactUrl}
          >
            Abrir contato
          </ExternalUrlAction>
          <ExternalUrlAction
            disabledTitle="Atendimento Chatwoot indisponível"
            icon="ri-chat-3-line"
            size="sm"
            title="Abrir atendimento no chatwoot"
            url={chatwootUrl}
          >
            Abrir atendimento
          </ExternalUrlAction>
          {canRequestDocumentAdjustment && (
            <HabitaButton
              disabled={documentos.length === 0 || isSubmittingAjuste}
              icon="ri-tools-line"
              onClick={() => openAjuste()}
              size="sm"
              variant="warning"
            >
              Solicitar ajuste
            </HabitaButton>
          )}
          <HabitaButton
            disabled={downloadableDocumentos.length === 0 || isBuildingZip}
            icon="ri-download-line"
            onClick={openDownloadDialog}
            size="sm"
            title={
              downloadableDocumentos.length === 0
                ? 'Disponível quando houver documentos validados'
                : 'Baixar documentos validados da pasta'
            }
          >
            Baixar pasta
          </HabitaButton>
          {canValidateFolder && (
            <HabitaButton
              disabled={isSubmittingValidacao}
              icon="ri-checkbox-circle-line"
              onClick={() => setValidarOpen(true)}
              size="sm"
              variant="success"
            >
              Validar pasta
            </HabitaButton>
          )}
        </div>
      </div>

      <div
        className={classNames(
          'grid grid-cols-1 gap-4',
          resumoCollapsed
            ? 'xl:grid-cols-[56px_minmax(0,1fr)]'
            : 'xl:grid-cols-[minmax(240px,300px)_minmax(0,1fr)]'
        )}
      >
        {(canRegisterPreAnalysisResult || currentPreAnalysisConfig) && (
          <section className={classNames(habitaSurfaceClass, 'border-primary/30 p-4 xl:col-span-2')}>
            {canRegisterPreAnalysisResult ? (
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="flex items-center gap-2 font-instrument text-sm font-semibold text-textPrimary">
                    <i className="ri-list-check-3 text-base text-primary" aria-hidden />
                    Resultado da pré-análise
                  </p>
                  <p className="mt-0.5 font-instrument text-xs text-textSecondary">
                    Registre o resultado da pré-análise desta pasta. Esta ação fica registrada na timeline de auditoria.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(Object.keys(preAnalysisDecisionConfigs) as PreAnalysisDecision[]).map((decision) => {
                    const config = preAnalysisDecisionConfigs[decision];

                    return (
                      <HabitaButton
                        key={decision}
                        disabled={isSubmittingPreAnalysis}
                        icon={config.icon}
                        onClick={() => openPreAnalysisDecision(decision)}
                        size="sm"
                        variant="secondary"
                        className={classNames('bg-card', config.outlineClassName)}
                      >
                        {config.title}
                      </HabitaButton>
                    );
                  })}
                </div>
              </div>
            ) : currentPreAnalysisConfig && (
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <i
                    className={classNames(currentPreAnalysisConfig.icon, 'mt-0.5 shrink-0 text-xl', currentPreAnalysisConfig.iconClassName)}
                    aria-hidden
                  />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-instrument text-sm font-semibold text-textPrimary">
                        Resultado da pré-análise
                      </p>
                      <PastaStatusBadge status={pastaStatusAtual} />
                    </div>
                    {currentPreAnalysisEvent?.justificativa && (
                      <p className="mt-1 font-instrument text-xs text-textSecondary">
                        Justificativa: {currentPreAnalysisEvent.justificativa}
                      </p>
                    )}
                  </div>
                </div>

                {canCorrectPreAnalysisResult && (
                  <HabitaButton
                    disabled={isSubmittingCorrection}
                    icon="ri-arrow-go-back-line"
                    onClick={() => setConfirmCorrectionOpen(true)}
                    size="sm"
                    variant="secondary"
                    className="border-primary/40 text-primary hover:bg-primary/10"
                  >
                    Corrigir resultado
                  </HabitaButton>
                )}
              </div>
            )}
          </section>
        )}

        <section
          className={classNames(
            habitaSurfaceClass,
            'min-w-0',
            !resumoCollapsed && 'flex max-h-[200vh] flex-col overflow-hidden',
            resumoCollapsed && 'overflow-hidden'
          )}
        >
          {resumoCollapsed ? (
            <button
              type="button"
              aria-label="Expandir resumo do cliente"
              title="Expandir resumo do cliente"
              onClick={() => setResumoCollapsed(false)}
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary xl:min-h-[220px] xl:flex-col xl:justify-start xl:px-2 xl:py-4"
            >
              <span className="flex min-w-0 items-center gap-2 xl:flex-col xl:gap-3">
                <i className="ri-file-text-line text-lg" aria-hidden />
                <span className="truncate font-instrument text-sm font-semibold xl:hidden">Resumo do cliente</span>
              </span>
              <i className="ri-arrow-down-s-line text-xl xl:hidden" aria-hidden />
              <i className="ri-arrow-right-s-line hidden text-xl xl:block" aria-hidden />
            </button>
          ) : (
            <>
              <HabitaSectionHeader title="Resumo do cliente" icon="ri-file-text-line" className="px-4 py-3">
                <button
                  type="button"
                  aria-label="Recolher resumo do cliente"
                  onClick={() => setResumoCollapsed(true)}
                  title="Recolher resumo do cliente"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-instrument text-sm font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary"
                >
                  <i className="ri-arrow-up-s-line text-base xl:hidden" aria-hidden />
                  <i className="ri-arrow-left-s-line hidden text-base xl:block" aria-hidden />
                </button>
              </HabitaSectionHeader>
              <div className={classNames(habitaDividerClass, 'min-h-0 flex-1 overflow-y-auto py-0')}>
                {resumo.length === 0 ? (
                  <p className="px-4 py-6 font-instrument text-sm text-textSecondary">
                    Nenhum dado consolidado com origem disponível.
                  </p>
                ) : filledFieldRowsFirst(resumo).map((item) => (
                  <div key={item.label} className="flex items-start justify-between gap-2 px-4 py-2 transition-colors hover:bg-accent">
                    <div className="min-w-0 flex-1">
                      <p className="font-instrument text-[10px] uppercase tracking-wide text-textSecondary">{item.label}</p>
                      <p className="whitespace-normal break-words font-instrument text-sm font-medium leading-tight text-textPrimary">{item.valor}</p>
                      <p className="whitespace-normal break-words font-instrument text-[10px] leading-tight text-textSecondary/75">
                        {item.origem}{item.documento !== '-' && ` · ${item.documento}`}{'docId' in item && item.docId ? ` - ID ${item.docId}` : ''}
                      </p>
                    </div>
                    <FieldStatusBadge status={item.status} />
                    <CopyValueButton label={item.label} value={item.valor} />
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <div className="min-w-0 space-y-4">
          <section className={habitaSurfaceClass}>
            <HabitaSectionHeader title="Documentos e extrações" icon="ri-folder-3-line" />
            <div className="p-5">
              {filesLoading && (
                <div className="mb-3 rounded-md border border-border-subtle bg-card p-2.5 font-instrument text-xs text-textSecondary">
                  Carregando dados extraídos dos documentos...
                </div>
              )}

              {filesError && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-2.5">
                  <p className="font-instrument text-xs text-amber-900">{filesError}</p>
                  <HabitaButton
                    className="mt-2"
                    icon="ri-refresh-line"
                    onClick={() => void ensureProcessFiles({ force: true })}
                    size="sm"
                    variant="ghost"
                  >
                    Tentar novamente
                  </HabitaButton>
                </div>
              )}

              {documentos.length === 0 && !filesLoading && !filesError ? (
                <div className="py-10 text-center">
                  <i className="ri-inbox-line text-4xl text-textSecondary/60" aria-hidden />
                  <p className="mt-2 font-instrument text-sm text-textSecondary">
                    Nenhum documento recebido. Aguardando envio do cliente.
                  </p>
                </div>
              ) : documentos.length > 0 ? (
                <div className="space-y-2">
                  {documentoGroups.map((group) => {
                    const hiddenCount = group.hiddenDocumentos.length;
                    const isExpanded = expandedDocumentoGroupId === group.id;
                    const showHidden = Boolean(showHiddenDocumentosByGroup[group.id]);
                    const groupActiveDoc = group.visibleDocumentos.find((documento) => documento.id === activeDocId) ??
                      group.visibleDocumentos[0];
                    const groupActivePreview = groupActiveDoc ? previewByDocId[groupActiveDoc.id] : undefined;
                    const hasStructuredExtractedView = groupActiveDoc
                      ? shouldRenderStructuredExtractedView(groupActiveDoc) && Boolean(groupActiveDoc.file)
                      : false;
                    const shouldShowRejectionReasonInFields = Boolean(
                      groupActiveDoc?.motivoRejeicao &&
                      !hasStructuredExtractedView &&
                      groupActiveDoc.campos.length === 0
                    );

                    return (
                      <div key={group.id} className="overflow-hidden rounded-lg border border-border-subtle bg-muted">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                          <button
                            type="button"
                            aria-expanded={isExpanded}
                            onClick={() => toggleDocumentoGroup(group)}
                            className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-accent"
                          >
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                              <i className={`${documentoCategoriaIcons[group.categoria]} text-base`} aria-hidden />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-instrument text-sm font-semibold text-textPrimary">{group.label}</p>
                              <p className="font-instrument text-[11px] text-textSecondary">
                                {group.categoryLabel} · {group.visibleDocumentos.length} de {group.documentos.length} arquivo{group.documentos.length > 1 ? 's' : ''}
                              </p>
                            </div>
                            <i
                              className={`${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xl text-textSecondary`}
                              aria-hidden
                            />
                          </button>

                          <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2 sm:justify-end sm:px-3 sm:pb-0">
                            {documentoStatusOrder.map((status) => {
                              const count = group.statusCounts[status] ?? 0;
                              return count > 0 ? (
                                <DocumentoStatusCounter key={status} count={count} status={status} />
                              ) : null;
                            })}
                            {hiddenCount > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setShowHiddenDocumentosByGroup((current) => ({
                                    ...current,
                                    [group.id]: !current[group.id],
                                  }))
                                }
                                className="inline-flex h-6 items-center gap-1 rounded-md px-2 font-instrument text-[10px] font-medium text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary"
                              >
                                <i className={showHidden ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden />
                                {showHidden ? 'Ocultar inválidos' : `Mostrar ${hiddenCount} inválido${hiddenCount > 1 ? 's' : ''}`}
                              </button>
                            )}
                          </div>
                        </div>

                        {isExpanded && groupActiveDoc && (
                          <div className="border-t border-border-subtle p-3">
                            {group.visibleDocumentos.length > 1 && (
                              <div className="mb-3 flex gap-2 overflow-x-auto pb-1">
                                {group.visibleDocumentos.map((documento) => {
                                  const isActive = groupActiveDoc.id === documento.id;

                                  return (
                                    <button
                                      key={documento.id}
                                      type="button"
                                      onClick={() => setActiveDocId(documento.id)}
                                      className={classNames(
                                        'flex min-w-[240px] items-center justify-between gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
                                        isActive
                                          ? 'border-primary bg-card shadow-sm'
                                          : 'border-border-subtle bg-card/70 hover:border-primary/50 hover:bg-card'
                                      )}
                                    >
                                      <span
                                        className={classNames(
                                          'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                                          isActive ? 'bg-accent text-primary' : 'bg-accent/70 text-textSecondary'
                                        )}
                                      >
                                        <i className={`${documentoIconClassName(documento)} text-sm`} aria-hidden />
                                      </span>
                                      <div className="min-w-0 flex-1">
                                        <p className="truncate font-instrument text-xs font-semibold text-textPrimary">
                                          {documento.nome}
                                        </p>
                                        <p className="font-instrument text-[10px] text-textSecondary/80">
                                          ID {documento.id} · {formatHabitaDateTime(documento.dataEnvio)}
                                        </p>
                                      </div>
                                      <DocumentoStatusBadge status={documento.status} />
                                    </button>
                                  );
                                })}
                              </div>
                            )}

                            <div
                              className={classNames(
                                'grid grid-cols-1 items-start gap-3',
                                groupActivePreview ? 'lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)]' : ''
                              )}
                            >
                              <div className="min-w-0 space-y-2">
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex min-w-0 items-start gap-2">
                                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-accent text-primary">
                                      <i className={`${documentoIconClassName(groupActiveDoc)} text-sm`} aria-hidden />
                                    </span>
                                    <div className="min-w-0">
                                      <h3 className="truncate font-instrument text-sm font-semibold text-textPrimary">
                                        {groupActiveDoc.nome}
                                      </h3>
                                      <p className="font-instrument text-xs text-textSecondary">
                                        ID {groupActiveDoc.id} · {formatHabitaDateTime(groupActiveDoc.dataEnvio)} · {groupActiveDoc.origem}
                                      </p>
                                    </div>
                                  </div>
                                  <DocumentoStatusBadge status={groupActiveDoc.status} />
                                </div>

                                {groupActiveDoc.motivoRejeicao && !shouldShowRejectionReasonInFields && (
                                  <div className="flex items-start gap-2 rounded-md border border-red-300/60 bg-red-500/10 p-2.5">
                                    <i className="ri-close-circle-line mt-0.5 shrink-0 text-base text-red-700" aria-hidden />
                                    <div>
                                      <p className="font-instrument text-xs font-semibold text-red-800 dark:text-red-100">
                                        {groupActiveDoc.tipoRejeicao === 'ocr' ? 'Rejeitado pelo OCR' : 'Rejeitado por regra de negócio'}
                                      </p>
                                      <p className="mt-0.5 font-instrument text-xs text-red-900 dark:text-red-100">{groupActiveDoc.motivoRejeicao}</p>
                                    </div>
                                  </div>
                                )}

                                <div
                                  className={classNames(
                                    'space-y-2 overflow-y-auto pr-1',
                                    groupActivePreview ? 'max-h-[760px]' : 'max-h-[520px]'
                                  )}
                                >
                                  {hasStructuredExtractedView && groupActiveDoc.file ? (
                                    <ProcessFileTypeExtractedView file={groupActiveDoc.file} />
                                  ) : groupActiveDoc.campos.length === 0 ? (
                                    shouldShowRejectionReasonInFields ? (
                                      <DocumentRejectionReasonCard documento={groupActiveDoc} />
                                    ) : (
                                      <div className="rounded-md border border-dashed border-border-subtle p-3 font-instrument text-xs text-textSecondary">
                                        Nenhum campo extraído disponível para este arquivo.
                                      </div>
                                    )
                                  ) : filledFieldRowsFirst(groupActiveDoc.campos).map((campo) => {
                                    const needsReview = campo.status === 'revisao';
                                    return (
                                      <div
                                        key={campo.label}
                                        className={`rounded-md border p-2.5 ${needsReview ? 'border-amber-300/60 bg-amber-500/10' : 'border-border-subtle bg-card'}`}
                                      >
                                        <div className="flex items-center justify-between gap-3">
                                          <p className="font-instrument text-[11px] uppercase tracking-wide text-textSecondary">{campo.label}</p>
                                          <div className="flex shrink-0 items-center gap-1">
                                            <FieldStatusBadge status={campo.status} />
                                            <CopyValueButton label={campo.label} value={campo.valor} />
                                          </div>
                                        </div>
                                        <p className="mt-1 font-instrument text-sm font-medium text-textPrimary">{campo.valor}</p>
                                        <p className="mt-0.5 font-instrument text-[11px] text-textSecondary">
                                          {campo.origem} · {campo.documento} · {confidenceLabel(campo.confianca)}
                                        </p>
                                        {campo.motivo && <p className="mt-1 font-instrument text-[11px] text-amber-800">{campo.motivo}</p>}
                                        {campo.override && (
                                          <p className="mt-1 font-instrument text-[11px] text-textSecondary">
                                            Revisado por {campo.override.revisor} em {campo.override.revisadoEm}
                                            {campo.override.valorOriginal !== campo.override.valor && (
                                              <> · OCR: <span className="line-through">{campo.override.valorOriginal}</span></>
                                            )}
                                          </p>
                                        )}
                                        <div className="mt-2 flex flex-wrap gap-2">
                                          {needsReview && (
                                            <HabitaButton
                                              icon="ri-eye-line"
                                              onClick={() => openReview(groupActiveDoc.id, campo)}
                                              size="sm"
                                            >
                                              Revisar campo
                                            </HabitaButton>
                                          )}
                                          {campo.override && (
                                            <HabitaButton
                                              icon="ri-arrow-go-back-line"
                                              onClick={() => revertOverride(groupActiveDoc.id, campo.label)}
                                              size="sm"
                                              variant="ghost"
                                            >
                                              Reverter revisão
                                            </HabitaButton>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {groupActivePreview && (
                                <DocumentPreview
                                  compact
                                  documento={groupActiveDoc}
                                  onRetry={() => void loadDocumentPreview(groupActiveDoc, { force: true })}
                                  preview={groupActivePreview}
                                />
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          </section>

          <AjustesDocumentaisSection
            ajustes={ajustesDocumentais}
            error={pendingItemsError}
            loading={pendingItemsLoading}
            onReload={() => void loadPendingItems({ force: true })}
          />
          <PastaTimelineSection events={timelineEvents} />
        </div>
      </div>

      <PastaDownloadDialog
        documentos={downloadableDocumentos}
        isBuildingZip={isBuildingZip}
        isZipCancelRequested={isZipCancelRequested}
        isOpen={downloadOpen}
        onClose={closeDownloadDialog}
        onDownload={() => void downloadSelectedDocumentsZip()}
        onPreviewDocument={previewDownloadDocument}
        onRetryPreview={(documento) => void loadDocumentPreview(documento, { force: true })}
        onToggleAll={toggleAllDownloadDocuments}
        onToggleDocument={toggleDownloadDocument}
        previewByDocId={previewByDocId}
        previewDocId={downloadPreviewDocId}
        selectedDocIds={downloadDocIds}
        zipProgress={zipProgress}
      />

      <HabitaDialog
        isOpen={reviewing != null}
        onClose={() => setReviewing(null)}
        title={reviewing ? `Revisar campo · ${reviewing.campo.label}` : 'Revisar campo'}
        description="Compare o valor extraído com o documento original e confirme, corrija ou rejeite."
        size="lg"
        footer={
          <>
            <HabitaButton onClick={() => setReviewing(null)} variant="ghost">
              Cancelar
            </HabitaButton>
            <HabitaButton onClick={() => applyReview('rejeitado')} variant="danger">
              Rejeitar campo
            </HabitaButton>
            <HabitaButton onClick={() => applyReview('validado')} variant="success">
              Validar valor
            </HabitaButton>
          </>
        }
      >
        {reviewing && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {reviewingDoc && (
              <DocumentPreview
                documento={reviewingDoc}
                onRetry={() => void loadDocumentPreview(reviewingDoc, { force: true })}
                preview={previewByDocId[reviewingDoc.id]}
              />
            )}
            <div className="space-y-3">
              <div className="rounded-md border border-border-subtle bg-accent p-3">
                <p className="font-instrument text-[11px] uppercase tracking-wide text-textSecondary">Extração original</p>
                <p className="mt-1 font-instrument text-sm font-medium text-textPrimary">{reviewing.campo.valor}</p>
                <p className="mt-0.5 font-instrument text-[11px] text-textSecondary">
                  Origem {reviewing.campo.origem} · {reviewing.campo.documento} · {confidenceLabel(reviewing.campo.confianca)}
                </p>
                {reviewing.campo.motivo && (
                  <p className="mt-1 font-instrument text-[11px] text-amber-800">{reviewing.campo.motivo}</p>
                )}
              </div>
              <HabitaTextInput
                value={editValue}
                onChange={(event) => setEditValue(event.target.value)}
                aria-label="Valor revisado"
              />
              <HabitaTextarea
                label="Nota da revisão (opcional)"
                rows={3}
                value={editNota}
                onChange={(event) => setEditNota(event.target.value)}
                placeholder="Ex.: confirmado com cliente via WhatsApp."
              />
              <div className="rounded-md border border-dashed border-border-subtle p-2 font-instrument text-[11px] text-textSecondary">
                A ação ficará registrada na trilha de auditoria com seu usuário e horário.
              </div>
            </div>
          </div>
        )}
      </HabitaDialog>

      <HabitaDialog
        isOpen={ajusteOpen}
        onClose={() => {
          if (!isSubmittingAjuste) {
            setAjusteOpen(false);
          }
        }}
        title="Solicitar ajuste documental"
        description="Registre os documentos que precisam de correção para a Chavinha cobrar o cliente."
        footer={
          <>
            <HabitaButton
              disabled={isSubmittingAjuste}
              onClick={() => setAjusteOpen(false)}
              variant="ghost"
            >
              Cancelar
            </HabitaButton>
            <HabitaButton
              disabled={isSubmittingAjuste}
              icon={isSubmittingAjuste ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}
              onClick={() => void submitAjuste()}
              variant="warning"
            >
              {isSubmittingAjuste ? 'Solicitando...' : 'Solicitar ajuste'}
            </HabitaButton>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <p className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">
              Documentos que precisam de ajuste
            </p>
            <div className="mt-1 max-h-56 overflow-auto rounded-md border border-border-subtle">
              {documentos.map((documento) => (
                <HabitaCheckbox
                  key={documento.id}
                  checked={ajusteDocIds.includes(documento.id)}
                  disabled={isSubmittingAjuste}
                  onCheckedChange={() =>
                    setAjusteDocIds((current) =>
                      current.includes(documento.id)
                        ? current.filter((docId) => docId !== documento.id)
                        : [...current, documento.id]
                    )
                  }
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-instrument text-sm font-medium text-textPrimary">{documento.nome}</p>
                    <p className="font-instrument text-[11px] text-textSecondary">
                      {documento.tipo} · {formatHabitaDateTime(documento.dataEnvio)}
                    </p>
                  </div>
                  <DocumentoStatusBadge status={documento.status} />
                </HabitaCheckbox>
              ))}
            </div>
            <p className="mt-1 font-instrument text-[11px] text-textSecondary">
              {ajusteDocIds.length} selecionado(s)
            </p>
          </div>
          <HabitaTextarea
            label="Justificativa (auditoria interna)"
            rows={4}
            value={ajusteJustificativa}
            disabled={isSubmittingAjuste}
            onChange={(event) => setAjusteJustificativa(event.target.value)}
            placeholder="Ex.: Holerite ilegível e conta de luz fora do prazo de 90 dias."
          />
          <div className="rounded-md border border-dashed border-border-subtle p-2 font-instrument text-[11px] text-textSecondary">
            Ao receber o reenvio, o Chavinha fará a revalidação automaticamente.
          </div>
        </div>
      </HabitaDialog>

      <HabitaDialog
        isOpen={Boolean(activePreAnalysisConfig)}
        onClose={closePreAnalysisDecisionDialog}
        title={activePreAnalysisConfig?.title ?? 'Registrar resultado'}
        description={activePreAnalysisConfig?.description}
        size="lg"
        footer={
          <>
            <HabitaButton
              disabled={isSubmittingPreAnalysis}
              onClick={closePreAnalysisDecisionDialog}
              variant="ghost"
            >
              Cancelar
            </HabitaButton>
            <HabitaButton
              disabled={isSubmittingPreAnalysis}
              icon={isSubmittingPreAnalysis ? 'ri-loader-4-line animate-spin' : activePreAnalysisConfig?.icon}
              onClick={() => void submitPreAnalysisDecision()}
              variant={activePreAnalysisConfig?.variant ?? 'success'}
            >
              {isSubmittingPreAnalysis ? 'Registrando...' : activePreAnalysisConfig?.confirmLabel ?? 'Confirmar'}
            </HabitaButton>
          </>
        }
      >
        {activePreAnalysisConfig && (
          <div className="space-y-4">
            <HabitaTextarea
              disabled={isSubmittingPreAnalysis}
              label={`Justificativa ${activePreAnalysisConfig.requiredJustification ? '(obrigatória)' : '(opcional)'}`}
              onChange={(event) => setPreAnalysisJustification(event.target.value)}
              placeholder="Registre observações da análise de crédito, quando necessário."
              rows={5}
              value={preAnalysisJustification}
            />

            <div>
              <p className="font-instrument text-[11px] font-medium uppercase tracking-wide text-textSecondary">
                Anexos (opcional)
              </p>
              <label
                htmlFor="pre-analysis-attachments"
                className="mt-1 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-border-subtle bg-muted px-3 py-4 font-instrument text-sm text-textSecondary transition-colors hover:border-primary/50 hover:bg-accent hover:text-textPrimary"
              >
                <i className="ri-attachment-2 text-base" aria-hidden />
                Clique para anexar arquivos
              </label>
              <input
                className="sr-only"
                disabled={isSubmittingPreAnalysis}
                id="pre-analysis-attachments"
                multiple
                onChange={addPreAnalysisAttachments}
                type="file"
              />

              {preAnalysisAttachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {preAnalysisAttachments.map((attachment) => (
                    <div key={attachment.id} className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-card px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate font-instrument text-sm font-medium text-textPrimary">{attachment.file.name}</p>
                        <p className="font-instrument text-xs text-textSecondary">{formatFileSize(attachment.file.size)}</p>
                      </div>
                      <button
                        type="button"
                        disabled={isSubmittingPreAnalysis}
                        onClick={() => removePreAnalysisAttachment(attachment.id)}
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-textSecondary transition-colors hover:bg-accent hover:text-textPrimary disabled:cursor-not-allowed disabled:opacity-60"
                        aria-label={`Remover ${attachment.file.name}`}
                      >
                        <i className="ri-close-line text-base" aria-hidden />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-md border border-dashed border-border-subtle p-3 font-instrument text-sm text-textSecondary">
              A ação ficará registrada na timeline de auditoria com seu usuário e horário. Você poderá corrigir o resultado depois.
            </div>
          </div>
        )}
      </HabitaDialog>

      <HabitaDialog
        isOpen={confirmCorrectionOpen}
        onClose={() => {
          if (!isSubmittingCorrection) {
            setConfirmCorrectionOpen(false);
          }
        }}
        title="Corrigir resultado da pré-análise"
        description="A pasta voltará para o status Pré-análise e o resultado atual será reaberto para correção. Esta ação ficará registrada no histórico."
        footer={
          <>
            <HabitaButton
              disabled={isSubmittingCorrection}
              onClick={() => setConfirmCorrectionOpen(false)}
              variant="ghost"
            >
              Cancelar
            </HabitaButton>
            <HabitaButton
              disabled={isSubmittingCorrection}
              icon={isSubmittingCorrection ? 'ri-loader-4-line animate-spin' : 'ri-arrow-go-back-line'}
              onClick={() => void submitPreAnalysisCorrection()}
              variant="warning"
            >
              {isSubmittingCorrection ? 'Corrigindo...' : 'Confirmar correção'}
            </HabitaButton>
          </>
        }
      >
        <div className="rounded-md border border-dashed border-border-subtle p-3 font-instrument text-sm text-textSecondary">
          O histórico anterior será mantido integralmente e a pasta ficará disponível novamente para registrar um novo resultado de pré-análise.
        </div>
      </HabitaDialog>

      <HabitaDialog
        isOpen={validarOpen}
        onClose={() => {
          if (!isSubmittingValidacao) {
            setValidarOpen(false);
          }
        }}
        title="Validar pasta"
        description={
          <>
            Confirme a validação da pasta de <strong className="font-semibold text-textPrimary">{cliente.nome}</strong>.
            {' '}Esta ação é registrada na timeline de auditoria.
          </>
        }
        size="lg"
        footer={
          <>
            <HabitaButton
              disabled={isSubmittingValidacao}
              onClick={() => setValidarOpen(false)}
              variant="ghost"
            >
              Cancelar
            </HabitaButton>
            <HabitaButton
              disabled={isSubmittingValidacao}
              icon={isSubmittingValidacao ? 'ri-loader-4-line animate-spin' : 'ri-checkbox-circle-line'}
              onClick={() => void confirmarValidacao()}
              variant="success"
            >
              {isSubmittingValidacao ? 'Validando...' : 'Confirmar validação'}
            </HabitaButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="overflow-hidden rounded-md border border-border-subtle">
            <div className="flex items-center justify-between gap-3 border-b border-border-subtle bg-muted px-3 py-2">
              <span className="font-instrument text-[11px] font-semibold uppercase tracking-wide text-textSecondary">
                Resumo da pasta
              </span>
              <span className="rounded-md border border-border-subtle bg-card px-2 py-0.5 font-instrument text-[11px] font-semibold text-textPrimary">
                {documentos.length} documento{documentos.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="max-h-72 overflow-y-auto divide-y divide-border-subtle">
              {validationDocumentSummary.length === 0 ? (
                <div className="px-3 py-6 text-center font-instrument text-sm text-textSecondary">
                  Nenhum documento vinculado à pasta.
                </div>
              ) : validationDocumentSummary.map((group) => (
                <div key={group.id} className="flex flex-col gap-2 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-instrument text-sm font-semibold text-textPrimary">{group.label}</p>
                    <p className="font-instrument text-xs text-textSecondary">
                      {group.quantity} documento{group.quantity === 1 ? '' : 's'} enviado{group.quantity === 1 ? '' : 's'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:justify-end">
                    {documentoStatusOrder.map((status) => {
                      const count = group.statuses[status] ?? 0;
                      if (count === 0) return null;

                      return (
                        <span
                          key={status}
                          className={`inline-flex shrink-0 rounded-md border px-2 py-0.5 font-instrument text-[10px] font-medium ${documentoStatusClasses[status]}`}
                        >
                          {documentoStatusLabels[status]}: {count}
                        </span>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-dashed border-border-subtle p-3 font-instrument text-sm text-textSecondary">
            Ao confirmar, a pasta será encaminhada automaticamente para{' '}
            <strong className="font-semibold text-textPrimary">Pré-análise</strong> e ficará disponível para o time de análise da Caixa.
          </div>
        </div>
      </HabitaDialog>
    </section>
  );
}
